import { useEffect, useMemo, useState, useRef, useCallback } from "react";
// import { fetchDetail, fetchEmployees, fetchMetadata, fetchUsers } from "../services/data";
import { fetchDetail, fetchEmployees, fetchMetadata, fetchWIP, deleteTask, triggerCleanup, fetchClientGroups, fetchActiveServices } from "../services/data";
import { useAuth } from "../auth/AuthContext";
import { useOutletContext } from "react-router-dom";
import { writeWorkflowEntry, formatTimestampWithTz, writeToPE } from "../services/api";
import { norm, normStrong, userNorm, cn, isInactiveEmp } from "../utils/norms";
import KpiShell from "../components/KPIShell";
import DataTable from "../components/DataTable";

// Import workflow statuses
import { WF, canTransition, keyify, buildEntry, updateWorkflow, IMPORT_OVERWRITABLE } from "../utils/workflow"

// Import excel import stuff
import {
  buildRprsTemplateWorkbook,
  downloadWorkbook,
  parseRprsImportFile,
  normalizeClientCode,
  normalizeServindex,
  runPool,
  isSafeClientCode,
  formatImportProblems,
} from "../utils/excelBulkImport";

// For bulk select
import StaffSelect from "../components/wizard/parts/StaffSelect";

/* NEW: scoped expansion UI */
import EmployeeDetailPanel from "../components/EmployeeDetailPanel";
//import styles from "../components/EmployeeDetailPanel.module.css";

// Components imports
import WizardShell from "../components/wizard/WizardShell";
import BulkElectiveModal from "../components/BulkElectiveModal";
import LoadingGame from "../components/LoadingGame";
import DraftsStep from "../components/wizard/DraftsStep";
import SummaryStep from "../components/wizard/SummaryStep";
import AdminSummaryStep from "../components/wizard/AdminSummaryStep";
// import PendingStep from "../components/wizard/PendingStep";

// Parts imports
// import RolePill from "../components/wizard/parts/RolePill"; // In DraftsStep and SummaryStep
// import StaffSelect from "../components/wizard/parts/StaffSelect"; // In DraftsStep
// import MoneyTriple from "../components/wizard/parts/MoneyCell"; // In SummaryStep
// import MiniFilterBar from "../components/wizard/parts/MiniFilterBar"; // In DraftsStep



const money = (v) =>
  "$" + Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const hoursFmt = v =>
  Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

// // Money with 0 decimals, try to remove -0 bug
// const money2 = (n) => {
//   const v = Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
//   const clean = Math.abs(v) < 0.5 ? 0 : v;      // threshold to zero
//   return "$" + clean.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
// };


/* ---------------- helpers ---------------- */
// const norm = s => (s ?? "").toString().trim().toLowerCase();

// // Slightly stronger normalization for name matching
// const normStrong = (s = "") =>
//   s
//     .toLowerCase()
//     .normalize("NFKC")
//     .replace(/\s+/g, " ")    // collapse runs of spaces
//     .replace(/[^\p{L}\p{N} ]+/gu, "")   // strip punctuation except spaces
//     .trim();

// // Normalization for usernames
// const userNorm = (s = "") =>
//   String(s)
//     .toLowerCase()
//     .normalize("NFKC")
//     .replace(/@.*$/, "")   // drop domain
//     .trim();

// // Check for the presence of column through different common name formats (ssms vs snowflake)
// // Example 'staffname' could access StaffName, STAFFNAME, staff_name, etc.
// // key canonicalizer: lower-case and strip non a–z/0–9
// const _kn = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
// /** Canonical column accessor */
// export const cn = (row, col, fallback = "") => {
//   const want = _kn(col);
//   for (const k in row) {
//     if (_kn(k) === want) {
//       const v = row[k];
//       return v == null ? fallback : v;
//     }
//   }
//   return fallback;
// };
  

const isNonEmpty = v => v !== undefined && v !== null && v !== "";

// Pull index if present; else try name → STAFFINDEX mapping.
// Resolve an employee index for a row/role (uses cn)
const resolveIndex = (row, role, nameToIndex) => {
  const idxKey  = { CP: 'cpindex', CM: 'cmindex', SP: 'spindex', SM: 'smindex' }[role];
  const nameKey = {
    CP: 'clientpartner', CM: 'clientmanager', SP: 'servicepartner', SM: 'servicemanager'
  }[role];

  const rawIdx = cn(row, idxKey, null);
  if (rawIdx != null && rawIdx !== '') {
    const n = Number(rawIdx);
    if (Number.isFinite(n)) return n;
  }
  const nm = cn(row, nameKey, '');
  return nm ? (nameToIndex.get(normStrong(nm)) ?? null) : null;
};
// function resolveIndex(row, role, nameToIndex) {
//   const idxField = role + "INDEX";
//   const nameField =
//     role === "CP" ? "CLIENTPARTNER" :
//     role === "CM" ? "CLIENTMANAGER" :
//     role === "SP" ? "SERVICEPARTNER" :
//                     "SERVICEMANAGER";

//   const rawIdx = row[idxField];
//   if (isNonEmpty(rawIdx)) {
//     const n = Number(rawIdx);
//     return Number.isFinite(n) ? n : null;
//   }
//   const nm = row[nameField];
//   return nameToIndex.get(norm(nm)) ?? null;
// }

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Flexible accessor for WIP hours (try common field names)
// const getWipHours = r =>
  // num(r.WIPHOURS); //?? r.WIP_HOURS ?? r.WIP_HRS ?? r.WIP_HOURS_TOTAL]);
const getWipHours = (r) => Number(cn(r, 'wiphours', 0)) || 0;


// Pull a service code tag from a detail row (normalized UPPER)
function getServiceTag(row) {
  const v =
    // row.SERVINDEX ??
    // row.SERVICELINECODE ??
    // row.SERVICELINE ??
    // row.SERVICECODE ??
    // row.SERVICE ??
    // row.SVCODE ??
    // row.SVC ??
    cn(row, "servindex") ||
    cn(row, "servicelinecode") ||
    cn(row, "serviceline") ||
    cn(row, "servicecode") ||
    cn(row, "service") ||
    cn(row, "svcode") ||
    cn(row, "svc") ||
    "";
  return String(v || "").trim().toUpperCase();
}

// helper: stable key for a row/task
const keyOf = (t) => t.wfKey ?? keyify(t); // workflow.js helper


function MoneyGroupHeader() {
  return (
    <div className="money-group-row">
      <div className="toolbar-left" />
      <div className="level-spacer" />
      <div className="dept-spacer" />
      <div className="office-spacer" />
      <div className="money-group-band">Managed Billings by Role</div>
    </div>
  );
}
function MultiSelectDropdown({
  options,
  selected,
  onChange,
  placeholder = "All",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const toggleOption = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((v) => v !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  const clearAll = () => onChange([]);

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length === options.length
      ? "All selected"
      : selected.join(", ");

  return (
    <div className="multi-select" ref={ref}>
      <button
        type="button"
        className="ab-select multi-select__button"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="multi-select__label">{displayText}</span>
        <span className="multi-select__chevron">▾</span>
      </button>

      {open && (
        <div className="multi-select__menu">
          <button
            type="button"
            className="multi-select__item multi-select__item--all"
            onClick={clearAll}
          >
            <input
              type="checkbox"
              readOnly
              checked={selected.length === 0}
            />
            <span>All</span>
          </button>
          {options.map((opt) => (
            <label key={opt} className="multi-select__item">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggleOption(opt)}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}



/* ---------- sorting helpers (outside component for stable reference) ----------- */
const cmp = (a, b) => (a > b) - (a < b);
const sortWithInactiveTop = (selector, isString = false) => (rowA, rowB) => {
  if (rowA.inactive !== rowB.inactive) return rowA.inactive ? -1 : 1; // pin inactive
  const a = selector(rowA);
  const b = selector(rowB);
  return isString ? String(a ?? "").localeCompare(String(b ?? "")) : cmp(a ?? 0, b ?? 0);
};

/* Small isolated search input — local state so keystrokes only re-render this component, */
/* not the entire Dashboard. Calls onChange(value) after 200ms debounce. */
function SearchInput({ value, onChange, ...props }) {
  const [localVal, setLocalVal] = useState(value || "");
  const timerRef = useRef(null);
  useEffect(() => { setLocalVal(value || ""); }, [value]);
  return (
    <input
      {...props}
      value={localVal}
      onChange={e => {
        const v = e.target.value;
        setLocalVal(v);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onChange(v), 200);
      }}
    />
  );
}

export default function Dashboard() {
  const [detail, setDetail] = useState([]);
  const [clientGroups, setClientGroups] = useState([]);
  const [emps, setEmps] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [wip, setWip] = useState([]);
  const [bulkIdx, setBulkIdx] = useState(null);
  // const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [wizardLoading, setWizardLoading] = useState(false);
  const pendingBulkKeysRef = useRef(new Set());

  const [adminDecisions, setAdminDecisions] = useState(new Map());
  const [importBusy, setImportBusy] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [submitToast, setSubmitToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Auto-dismiss submit toast after 8 seconds
  useEffect(() => {
    if (!submitToast) return;
    const t = setTimeout(() => setSubmitToast(false), 8000);
    return () => clearTimeout(t);
  }, [submitToast]);
  const importInputRef = useRef(null);

  const { isSuperUser, email: currentUserEmail, username: CURRENT_USER } = useAuth();
  const { isReassignAdmin } = useOutletContext() || {};
  // ADMIN* dept staff act as reassignment superusers (scoped, not global). Trello #263.
  const isAdmin = isSuperUser || isReassignAdmin;

  // slide-up panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const togglePanel = () => setPanelOpen(o => !o);
  const closePanel = () => setPanelOpen(false);
  const [bulkElectiveOpen, setBulkElectiveOpen] = useState(false);
  // Reassignment icon URL
  const ICON_URL = "https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/images/ReassignmentIcon.svg";


  // Bottom tray height (in viewport units). Starts at 50% of screen.
  const [trayVh, setTrayVh] = useState(50);

  // KPI row visibility
  const [kpisCollapsed, setKpisCollapsed] = useState(false);

  // Dragging refs/state for the resize handle
  const draggingRef  = useRef(false);
  const startYRef    = useRef(0);
  const startVhRef   = useRef(50);
  const pendingVhRef = useRef(null);  // tracks latest vh during drag

  // Stable refs so listeners are registered once and never re-bound
  const dragMoveRef = useRef(null);
  const dragEndRef  = useRef(null);

  // Config: min/max height (in vh)
  const MIN_VH = 20;
  const MAX_VH = 90;

  const onDragStart = (clientY) => {
    draggingRef.current = true;
    startYRef.current   = clientY;
    startVhRef.current  = trayVh;
    document.body.style.userSelect = "none";
    const el = document.getElementById("reassign-bottom-tray");
    if (el) el.classList.add("tray-dragging"); // suppress CSS transition while dragging
  };

  const onDragMove = (clientY) => {
    if (!draggingRef.current) return;
    const deltaPx = startYRef.current - clientY;          // drag up = increase height
    const vhPerPx = 100 / window.innerHeight;
    const next = Math.max(MIN_VH, Math.min(MAX_VH, startVhRef.current + deltaPx * vhPerPx));
    pendingVhRef.current = next;
    // Write directly to the DOM — no React re-render = smooth 60 fps drag
    const el = document.getElementById("reassign-bottom-tray");
    if (el) el.style.height = `${next}vh`;
  };

  const onDragEnd = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.userSelect = "";
    const el = document.getElementById("reassign-bottom-tray");
    if (el) el.classList.remove("tray-dragging");
    // One React state sync at drag-end keeps the value correct on next render
    if (pendingVhRef.current != null) {
      setTrayVh(pendingVhRef.current);
      pendingVhRef.current = null;
    }
  };

  // Keep function refs current every render (avoids stale-closure in listeners)
  dragMoveRef.current = onDragMove;
  dragEndRef.current  = onDragEnd;

  // Register global listeners ONCE at mount; refs keep handlers fresh
  useEffect(() => {
    const mm = (e) => dragMoveRef.current(e.clientY);
    const mu = () => dragEndRef.current();
    const tm = (e) => dragMoveRef.current(e.touches[0].clientY);
    const tu = () => dragEndRef.current();

    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup",   mu);
    window.addEventListener("touchmove", tm, { passive: false });
    window.addEventListener("touchend",  tu);

    return () => {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup",   mu);
      window.removeEventListener("touchmove", tm);
      window.removeEventListener("touchend",  tu);
    };
  }, []); // empty — refs ensure we always call the latest function



  /* ---------------- filters state ---------------- */
  const [filters, setFilters] = useState({
    q: "",
    // dept: "All",
    // office: "All",
    // level: "All",
    dept: [],
    office: [],
    level: [],
    inactiveOnly: false,
    includeZero: false,
    mgmtOnly: false,         // Management Group Only
    minTotal: "",
    sortBy: "total",         // total | cp | cm | sp | sm | name
    sortDir: "desc",         // desc | asc
  });

  // --- Persist expanded rows across re-renders (tray drag, filters, etc.)
  const [openStaff, setOpenStaff] = useState(new Set());
  const toggleStaff = useCallback((staffIndex) => {
    setOpenStaff(prev => {
      const next = new Set(prev);
      if (next.has(staffIndex)) next.delete(staffIndex);
      else next.add(staffIndex);
      return next;
    });
  }, []);


  // People-focus filter: when non-empty, only show these staff in the main table
  const [focusStaff, setFocusStaff] = useState(new Set());
  const clearFocus = () => setFocusStaff(new Set());


  const update = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  // For mult-select filters
  const updateMulti = (k) => (e) => {
    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
    setFilters(f => ({ ...f, [k]: selected }));
  };


  useEffect(() => {
    (async () => {
      try {
        const [d, e, m, w, g/*, u*/] = await Promise.all([fetchDetail(), fetchEmployees(), fetchMetadata(), fetchWIP(), fetchClientGroups()]);//, fetchUsers()]);
        setDetail(Array.isArray(d) ? d : []);
        setEmps(Array.isArray(e) ? e : []);
        setMetadata(Array.isArray(m) ? m : []);
        setWip(Array.isArray(w) ? w : []);
        setClientGroups(Array.isArray(g) ? g : []);
        triggerCleanup();
        // setUsers(Array.isArray(u) ? u : []);
      } catch (err) {
        const msg = err.message || String(err);
        if (msg.includes("403") || msg.includes("Unauthorized") || msg.includes("Access denied") || msg.includes("not authorized")) {
          setAuthError(msg);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);



  /* ---------------- KPIs from raw detail ---------------- */
  // const kpi = useMemo(() => ({
  //   billed: detail.reduce((s, r) => s + Number(r.BILLED || 0), 0),
  //   wip: detail.reduce((s, r) => s + Number(r.WIP || 0), 0),
  //   clients: new Set(detail.map(r => r.CLIENTCODE)).size,
  //   staff: new Set(
  //     detail
  //       .flatMap(r => [r.CMINDEX, r.CPINDEX, r.SMINDEX, r.SPINDEX])
  //       .filter(isNonEmpty)
  //   ).size
  // }), [detail]);

  /* ---------------- lookups from employee roster ---------------- */
  const { nameToIndex, empByIndex, userByIndex, userToName, nameToUser, userToIndex, deptOptions, officeOptions, levelOptions } =
    useMemo(() => {
      const nameToIndex = new Map();
      const empByIndex = new Map();
      const userByIndex = new Map();
      const userToName = new Map();
      const nameToUser = new Map();
      const userToIndex = new Map();
      const deptSet = new Set();
      const officeSet = new Set();
      const levelSet = new Set();

      emps.forEach(e => {
        /*
        const idx = Number(e.STAFFINDEX);
        if (e.STAFFNAME) nameToIndex.set(norm(e.STAFFNAME), idx);
        if (Number.isFinite(idx)) empByIndex.set(idx, e);
        if (Number.isFinite(idx)) userByIndex.set(idx, userNorm(e.STAFFUSER));
        if (e.STAFFDEPARTMENT) deptSet.add(e.STAFFDEPARTMENT);
        if (e.STAFFOFFICE) officeSet.add(e.STAFFOFFICE);
        if (e.LEVELDESC || e.LEVELCODE) levelSet.add(e.LEVELDESC || e.LEVELCODE);
        */

        const idx = Number(cn(e, "staffindex"));                   // STAFFINDEX / StaffIndex / staff_index

        // nameToIndex map, normalized employee name -> STAFFINDEX
        const name = cn(e, "staffname");                           // STAFFNAME / StaffName / staff_name
        if (name) nameToIndex.set(normStrong(name), idx);

        // empByIndex map, STAFFINDEX -> full employee row
        if (Number.isFinite(idx)) empByIndex.set(idx, e);

        // userByIndex map, STAFFINDEX -> normalized username
        const user = cn(e, "staffuser");                      // StaffUser / STAFF_USER
        if (Number.isFinite(idx)) userByIndex.set(idx, userNorm(user || ""));

        // userToName map, normalized username -> employee name
        if (user) userToName.set(userNorm(user), name);

        // nameToUser map, normalized username -> employee name
        if (name && user) nameToUser.set(normStrong(name), userNorm(user));

        // userToIndex map, normalized username -> STAFFINDEX 
        if (user && Number.isFinite(idx)) userToIndex.set(userNorm(user), idx);

        // dept/office/level sets
        const dept   = cn(e, "staffdepartment");                   // STAFFDEPARTMENT / StaffDepartment
        const office = cn(e, "staffoffice");
        const level  = cn(e, "leveldesc") || cn(e, "levelcode");
        if (dept)   deptSet.add(dept);
        if (office) officeSet.add(office);
        if (level)  levelSet.add(level);
      });

      // const toSorted = set => ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
      const toSorted = set => Array.from(set).sort((a, b) => a.localeCompare(b));

      return {
        nameToIndex,
        empByIndex,
        userByIndex,
        userToName,
        nameToUser,
        userToIndex,
        deptOptions: toSorted(deptSet),
        officeOptions: toSorted(officeSet),
        levelOptions: toSorted(levelSet),
      };
    }, [emps]);

  // Helpers (prefer index when you have it)
  const usernameByIndex = (idx) => userByIndex.get(Number(idx)) ?? null;
  const usernameOf = (fullName) => {
    const idx = nameToIndex.get(normStrong(fullName));
    return idx ? usernameByIndex(idx) : null;
  };

  /* ---------------- aggregate billings per employee by role ---------------- */
  const rows = useMemo(() => {
    const out = new Map(); // key: STAFFINDEX

    const ensure = (empIdx) => {
      if (!out.has(empIdx)) {
        const e = empByIndex.get(empIdx) || {};
        // const active = cn(e, "active_employee", null); // boolean or "Yes/No"
        const active = !isInactiveEmp(e);
        out.set(empIdx, {
          empIdx,
          name: cn(e, "staffname", "(Unknown)"),
          level: cn(e, "leveldesc") || cn(e, "levelcode") || "",
          dept: cn(e, "staffdepartment") || "",
          office: cn(e, "staffoffice") || "",
          // inactive: (active === false) || (String(active).toLowerCase() === "no"),
          inactive: isInactiveEmp(e),
          //TODO: elective: hasElectives(e)
          mgmtGroup: String(cn(e, "ismgmtgroup", "")).toLowerCase() === "yes",
          // billed
          cp: 0, cm: 0, sp: 0, sm: 0,
          // total WIP hours
          wipHours: 0,
        });
      }
      // if (!out.has(empIdx)) {
      //   const e = empByIndex.get(empIdx) || {};
      //   out.set(empIdx, {
      //     empIdx,
      //     name: e?.STAFFNAME || "(Unknown)",
      //     level: e?.LEVELDESC || e?.LEVELCODE || "",
      //     dept: e?.STAFFDEPARTMENT || "",
      //     office: e?.STAFFOFFICE || "",
      //     inactive: e?.ACTIVE_EMPLOYEE === false,
      //     mgmtGroup: (e?.ISMGMTGROUP || "").toString().toLowerCase() === "yes",
      //     // billed
      //     cp: 0, cm: 0, sp: 0, sm: 0,
      //     // total WIP hours
      //     wipHours: 0,
      //   });
      // }
      return out.get(empIdx);
    };

    for (const r of detail) {
      // const billedAmt = num(r.BILLED);
      const billedAmt = num(cn(r, "billed") || 0);
      const wipHrs    = getWipHours(r);

      // If there’s *either* billed $ or WIP hours, attribute by role indices
      if (billedAmt || wipHrs) {
        const idxCP = resolveIndex(r, "CP", nameToIndex);
        if (idxCP != null) { const x = ensure(idxCP); x.cp += billedAmt; x.wipHours += wipHrs; }

        const idxCM = resolveIndex(r, "CM", nameToIndex);
        if (idxCM != null) { const x = ensure(idxCM); x.cm += billedAmt; x.wipHours += wipHrs; }

        const idxSP = resolveIndex(r, "SP", nameToIndex);
        if (idxSP != null) { const x = ensure(idxSP); x.sp += billedAmt; x.wipHours += wipHrs; }

        const idxSM = resolveIndex(r, "SM", nameToIndex);
        if (idxSM != null) { const x = ensure(idxSM); x.sm += billedAmt; x.wipHours += wipHrs; }
      }
    }

    // include roster entries so inactive flag & meta are available
    for (const e of emps) {
      const idx = Number(cn(e, "staffindex"));
    if (Number.isFinite(idx) && !out.has(idx)) {
      //const active = cn(e, "active_employee", null); // could be boolean or string
      const active = !isInactiveEmp(e);
      out.set(idx, {
        empIdx: idx,
        name: cn(e, "staffname"),
        level: cn(e, "leveldesc") || cn(e, "levelcode") || "",
        dept: cn(e, "staffdepartment") || "",
        office: cn(e, "staffoffice") || "",
        // inactive: (active === false) || (String(active).toLowerCase() === "no"),
        inactive: isInactiveEmp(e),
        mgmtGroup: String(cn(e, "ismgmtgroup", "")).toLowerCase() === "yes",
        cp: 0, cm: 0, sp: 0, sm: 0,
      });
    }
      // const idx = Number(e.STAFFINDEX);
      // if (Number.isFinite(idx) && !out.has(idx)) {
      //   out.set(idx, {
      //     empIdx: idx,
      //     name: e.STAFFNAME,
      //     level: e.LEVELDESC || e.LEVELCODE || "",
      //     dept: e.STAFFDEPARTMENT || "",
      //     office: e.STAFFOFFICE || "",
      //     inactive: e.ACTIVE_EMPLOYEE === false,
      //     mgmtGroup: (e?.ISMGMTGROUP || "").toString().toLowerCase() === "yes",
      //     cp: 0, cm: 0, sp: 0, sm: 0
      //   });
      // }
    }

    return [...out.values()].map(r => ({
      ...r,
      total: r.cp + r.cm + r.sp + r.sm,      // dollars
      // r.wipHours already holds total hours
    }));
  }, [detail, emps, nameToIndex, empByIndex]);

  // For client detail wip hover
  const wipByClientCode = useMemo(() => {
    const m = new Map();
    for (const row of wip) {
      const code = String(cn(row, "clientcode", "")).trim();
      if (!code) continue;
      if (!m.has(code)) m.set(code, []);
      m.get(code).push(row);
    }
    return m;
  }, [wip]);

  /* ---------------- filters applied to rows ---------------- */
  const filteredRows = useMemo(() => {
    const q = norm(filters.q);
    const minTotal = filters.minTotal === "" ? 0 : Number(filters.minTotal) || 0;

    let list = rows.filter(r => {
      if (!filters.includeZero && r.total <= 0) return false;
      if (filters.inactiveOnly && !r.inactive) return false;
      if (filters.mgmtOnly && !r.mgmtGroup) return false;

      // if (filters.dept !== "All" && r.dept !== filters.dept) return false;
      // if (filters.office !== "All" && r.office !== filters.office) return false;
      // if (filters.level !== "All" && r.level !== filters.level) return false;
      // Change to multi-select
      // If nothing is selected -> treat as "All" (no filter for that field)
      if (filters.dept.length && !filters.dept.includes(r.dept)) return false;
      if (filters.office.length && !filters.office.includes(r.office)) return false;
      if (filters.level.length && !filters.level.includes(r.level)) return false;

      if (minTotal > 0 && r.total < minTotal) return false;
      if (q) {
        const hay = `${r.name} ${r.dept} ${r.office} ${r.level}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    // default list order: inactive first, then by chosen key/dir
    const dir = filters.sortDir === "asc" ? 1 : -1;
    const key = filters.sortBy;
    list.sort((a, b) => {
      if (a.inactive !== b.inactive) return a.inactive ? -1 : 1;
      if (key === "name") return dir * a.name.localeCompare(b.name);
      return dir * ((b[key] ?? 0) - (a[key] ?? 0));
    });

    // If a focus filter is active, keep only those staff
    if (focusStaff.size) {
      list = list.filter(r => focusStaff.has(r.empIdx));
    }
    return list;

  }, [rows, filters, focusStaff]);

  /* ---------------- reassignment KPI (based on filtered view) -------------- */
  const reassignmentTotal = useMemo(
    () => filteredRows.filter(r => r.inactive).reduce((s, r) => s + r.total, 0),
    [filteredRows]
  );

  // ------- KPIs derived from the FILTERED view -------
  const billedFiltered = useMemo(
    () => filteredRows.reduce((s, r) => s + (Number(r.total) || 0), 0),
    [filteredRows]
  );

  const avgBilledPerEmployee = useMemo(
    () => (filteredRows.length ? billedFiltered / filteredRows.length : 0),
    [billedFiltered, filteredRows.length]
  );

  const wipHoursFiltered = useMemo(
    () => filteredRows.reduce((s, r) => s + num(r.wipHours), 0),
    [filteredRows]
  );

  const avgWipHoursPerEmployee = useMemo(
    () => (filteredRows.length ? wipHoursFiltered / filteredRows.length : 0),
    [wipHoursFiltered, filteredRows.length]
  );

  // ---------------- Metadata lookup and name->username map ----------------
  // Map KEY -> metadata row
  // const metadataByKey = useMemo(() => {
  //   const m = new Map();
  //   for (const w of metadata) {
  //     if (w?.KEY) m.set(w.KEY, w);
  //   }
  //   return m;
  // }, [metadata]);

  // Map "First Last" -> username (to build the KEY)


  // Old maps from separate users blob
  /*
  const nameToUser = useMemo(() => {
    const m = new Map();
    for (const u of users) {
      const uname = u?.user;
      if (u?.fullname && uname) m.set(normStrong(u.fullname), uname);
    }
    return m;
  }, [users]);

  const userToName = useMemo(() => {
    const m = new Map();
    for (const u of users) {
      const uname = u?.user;
      if (u?.fullname && uname) m.set(uname, u.fullname);
    }
    return m;
  }, [users]);
  */

  // Helper to build normalized metadata key
  // Metadata key in form: 05201-001_SM_INDTAX_avinston
  // const buildNormMetaKey = (clientCode, role, service, staffName) => {
  //   const uname = nameToUser.get(normStrong(staffName)) || "unknownuser";
  //   const normKey = `${clientCode}_${role}_${service}_${uname}`;
  //   return normKey;
  // }

  // Build the canonical metadata key from *parts*.
  // Example: buildNormMetaKey("05201-001", "SM", "INDTAX", "avinston")
  // => "05201-001_SM_INDTAX_avinston"
  // const keyify = (clientCode, role, service, username) => {
  //   // Normalizers for the 3 “coded” parts of the KEY.
  //   // Keep username lower-case (your JSON uses lower-case usernames)
  //   const normCode  = (s = "") => String(s).trim().toUpperCase();
  //   const normRole  = (s = "") => String(s).trim().toUpperCase();    // "CP" | "CM" | "SP" | "SM"
  //   const normSvc   = (s = "") => String(s).trim().toUpperCase();    // e.g., "INDTAX" or "" (for CP/CM)
  //   // const normUser  = (s = "") => String(s).trim().toLowerCase();    // usernames are lower-case
  //   return `${normCode(clientCode)}_${normRole(role)}_${normSvc(service)}_${userNorm(username)}`;
  // }

  // Index metadata rows by canonical key
  const metadataByKey = useMemo(() => {
    const m = new Map();
    for (const w of metadata) {
      const key = cn(w, "key", null);
      const existing = m.get(key);
      if (!existing) { m.set(key, w); continue; }
      // Keep the entry with the latest timestamp so newest status wins
      const existingTs = cn(existing, "updated_ts", "") || "";
      const newTs      = cn(w, "updated_ts", "") || "";
      if (newTs > existingTs) m.set(key, w);

      // // If the JSON already has parts, use them; otherwise fall back to w.KEY
      // // if (w?.CLIENTCODE && w?.ROLE_FOR_REASSIGNMENT && w?.USER_FOR_REASSIGNMENT) {
      // //   const k = keyify(w.CLIENTCODE, w.ROLE_FOR_REASSIGNMENT, w.SERVICE ?? "NA", w.USER_FOR_REASSIGNMENT);
      // //   m.set(k, w);
      // const cc   = cn(w, "clientcode", null);
      // const role = cn(w, "role_for_reassignment", null);
      // const user = cn(w, "user_for_reassignment", null);
      // const svc  = cn(w, "service", "NA");

      // if (cc && role && user) {
      //   const k = keyify(cc, role, svc || "NA", user);
      //   m.set(k, w);
      //   continue;
      // }

      // // fallback to KEY string if present
      // const key = cn(w, "key", null);
      // if (key) {
      //   const [cc2, role2, svc2 = "", user2 = ""] = String(key).split("_");
      //   const k = keyify(cc2, role2, svc2, user2);
      //   m.set(k, w);
      // }
    }
    return m;
  }, [metadata]);


  // Debugging metadata keys
  useEffect(() => {
    const keys = [...metadataByKey.keys()];
    const nullish = keys.filter(k => !k).length;
    console.log("[RPRS] metadataByKey size:", metadataByKey.size, "null/empty keys:", nullish);

    // optional: show a few keys to eyeball formatting
    console.log("[RPRS] metadataByKey sample:", keys.filter(Boolean).slice(0, 10));
    console.log("[RPRS] metadataByKey:", keys);
  }, [metadataByKey]);


  // Return either a single canonical key string OR an array of keys
  // when a row has multiple service tags (SP/SM).
  // If you prefer to only use one tag, pick the first (see comment).
  // const buildNormMetaKey = (clientCode, role, service, staffName) => {
  //   // 1) username from "First Last"
  //   const username = nameToUser.get(normStrong(staffName)) || "unknownuser";

  //   // 2) service value
  //   const svc = (role === "SP" || role === "SM") ? (service || "NA") : "NA";
  //   // let service = "";
  //   // if (role === "SP" || role === "SM") {
  //   //   // serviceTagsOrSvc may be an array (e.g., t.serviceTags) or a string
  //   //   if (Array.isArray(serviceTagsOrSvc)) {
  //   //     // OPTION A: return multiple keys (for all services the row represents)
  //   //     // return serviceTagsOrSvc.map(s => keyify(clientCode, role, s, username));

  //   //     // OPTION B (simple): just use the first tag if present
  //   //     service = serviceTagsOrSvc[0] ?? "NA";
  //   //   } else {
  //   //     service = serviceTagsOrSvc || "NA";
  //   //   }
  //   // } else {
  //   //   // CP/CM have no service
  //   //   service = "NA";
  //   // }

  //   // 3) build the canonical key
  //   return keyify(clientCode, role, svc, username);
  // };

  // Map "First Last" -> username  TODO check the new one works
  // const usernameOf = (fullName) => nameToUser.get(normStrong(fullName)) || null;

  // Current CP username for a client (from detail)
  const getClientPartnerUsername = (clientCode) => {
    for (const r of detail) {
      // if (r.CLIENTCODE !== clientCode) continue;
      if (cn(r, "clientcode") !== clientCode) continue;
      const ix = resolveIndex(r, "CP", nameToIndex);
      if (Number.isFinite(ix)) {
        // const nm = empByIndex.get(ix)?.STAFFNAME || r.CLIENTPARTNER;
        const nm = cn(empByIndex.get(ix) || {}, "staffname") || cn(r, "clientpartner");
        const u  = usernameOf(nm);
        if (u) return u;
      }
    }
    return null; // fallback applied later
  };

  // “Job originator” username for CP tasks
  const getOriginatorUsername = (clientCode) => {
    for (const r of detail) {
      if (cn(r, "clientcode") !== clientCode) continue;
      // if (r.CLIENTCODE !== clientCode) continue;
      // const u = usernameOf(r.CLIENTORIGINATOR);
      const u = usernameOf(cn(r, "clientoriginator"));
      if (u) return u;
    }
    return null;
  };

  // Pick the “TO_USER” for each task once
  // const routeToFor = (t) =>
  //   (t.role === "CP" ? getOriginatorUsername(t.clientCode)
  //                   : getClientPartnerUsername(t.clientCode)) || "sysadmin";
  const routeToFor = (t) => {
    const orig_user = getOriginatorUsername(t.clientCode);
    const cp_user   = getClientPartnerUsername(t.clientCode);
    const orig_idx = userToIndex.get(orig_user);
    const orig_emp = empByIndex.get(orig_idx);
    const cp_idx = userToIndex.get(cp_user);
    const cp_emp = empByIndex.get(cp_idx);
    const inactive_orig = orig_emp ? isInactiveEmp(orig_emp) : true;
    const inactive_cp = cp_emp ? isInactiveEmp(cp_emp) : true;
    if (t.role === "CP") {
      if (inactive_orig) return "sysadmin";
      else return orig_user || "sysadmin"; 
    }
    else {
      if (inactive_cp && inactive_orig) return "sysadmin";
      else if (inactive_cp && !inactive_orig) return orig_user || "sysadmin";
      else return cp_user || "sysadmin";
    }
  };

  // Seed initial workflow metadata for tasks that are missing it
  // async function seedMissingWorkflowMetadata(tasks) {
  //   const created = [];

  //   for (const t of tasks) {
  //     // USER_FOR_REASSIGNMENT (current inactive holder → username)
  //     // const userFor = usernameOf(t.currentStaffName) || "unknownuser";
  //     // const service = firstServiceFor(t);
  //     // const key     = keyify(t.clientCode, t.role, service, userFor);
  //     const userFor =
  //       usernameByIndex?.(t.currentStaffIdx) ||
  //       usernameOf(t.currentStaffName) ||
  //       "unknownuser";
  //     const service = (t.role === "SP" || t.role === "SM") ? (t.service || "NA") : "NA";
  //     const key     = keyify(t.clientCode, t.role, service, userFor);

  //     // Already have metadata? skip
  //     if (metadataByKey.has(key)) continue;

  //     // TO_USER rule: CP -> job originator; else -> current client partner
  //     const toUser =
  //       (t.role === "CP"
  //         ? getOriginatorUsername(t.clientCode)
  //         : getClientPartnerUsername(t.clientCode)) || "sysadmin";

  //     const entry = {
  //       CLIENTCODE: t.clientCode,
  //       FROM_USER:  "sysadmin",
  //       KEY:        key,
  //       ROLE_FOR_REASSIGNMENT: t.role,          // "CP" | "CM" | "SP" | "SM"
  //       SERVICE:    service,                    // "NA" for CP/CM, single service per task
  //       TO_USER:    toUser,
  //       UPDATED_TS: formatTimestampWithTz(new Date()),
  //       USER_FOR_REASSIGNMENT: userFor,
  //       NEW_STAFF_SELECTION:   "",              // blank initially
  //       WORKFLOW:   "To be reassigned",
  //     };

  //     created.push(entry);
  //   }

  //   if (!created.length) return [];

  //   // Write each JSON to blob (one file per entry)
  //   await Promise.all(created.map(writeWorkflowEntry));

  //   // Merge into local metadata so admin columns populate immediately
  //   setMetadata((prev) => [...prev, ...created]);

  //   return created;
  // }

  // Seed initial workflow metadata for tasks that are missing it
  async function seedMissingWorkflowMetadata(tasks) {
    // 1) figure out what’s actually missing (by KEY)
    // const toSeed = tasks.filter(t => !metadataByKey.has(keyify(t)));
    const toSeed = tasks.filter(t => {
      if (!metadataByKey.has(t.key)) return true;
      // Re-seed elective tasks whose prior metadata has a terminal status
      if (t.isElective) {
        const wf = cn(metadataByKey.get(t.key), 'workflow', null);
        return wf === WF.DRAFT_ACCEPTED || wf === WF.DRAFT_REJECTED;
      }
      return false;
    });

    console.group("[RPRS] seedMissingWorkflowMetadata");
    console.log("panelOpen:", panelOpen, "loading:", loading);
    console.log("tasks:", tasks.length, "toSeed:", toSeed.length);
    console.log("toSeed keys:", toSeed.map(t => t.key).slice(0, 25));
    // show one task + whether map has it
    if (toSeed[0]) console.log("example:", toSeed[0].key, "has?", metadataByKey.has(toSeed[0].key), toSeed[0]);
    console.groupEnd();

    if (toSeed.length === 0) return;

    // 2) pick the “TO_USER” for each task once
    // const routeToFor = (t) =>
    //   (t.role === "CP" ? getOriginatorUsername(t.clientCode)
    //                   : getClientPartnerUsername(t.clientCode)) || "sysadmin";

    // 3) create & write all entries concurrently
    const entries = await Promise.all(
      toSeed.map(t =>
        updateWorkflow({
          //intent: "seed",
          intent: t.isElective ? "elective" : "seed",
          task: t,
          actor: "sysadmin",
          routeTo: routeToFor(t),
          empByIndex
        })
      )
    );

    // debug
    console.log("[RPRS] seeded entries keys:", entries.map(e => e.KEY ?? e.key).slice(0, 25));

    // 4) append to metadata once (1 state change → 1 recompute)
    setMetadata(prev => prev.concat(entries));
  }

  // async function seedMissingWorkflowMetadata(tasks) {
  //   for (const t of tasks) {
  //     // const service = firstServiceFor(t);
  //     // const key     = keyify(t.clientCode, t.role, service, t.currentUsername);
  //     const key = keyify(t)

  //     // Already have metadata? skip
  //     if (metadataByKey.has(key)) continue;

  //     // TO_USER rule: CP -> job originator; else -> current client partner
  //     const toUser =
  //       (t.role === "CP"
  //         ? getOriginatorUsername(t.clientCode)
  //         : getClientPartnerUsername(t.clientCode)) || "sysadmin";

  //     const entry = await updateWorkflow({
  //       intent: 'seed',
  //       task: t,                   // the task object from reassignmentTasks
  //       actor: 'sysadmin',
  //       routeTo: toUser,
  //       empByIndex
  //     });

  //     // Merge into local metadata so admin columns populate immediately
  //     setMetadata((prev) => [...prev, entry]);
  //   }
  // }

  // Function to submit partner reassignment drafts and set them to "Draft change needs review" workflow status
  // async function submitDraftsAsOwner(drafts, empByIndex, currentUsername) {
  //   const entries = [];

  //   for (const { task, newStaffIdx } of drafts.values()) {
  //     if (!newStaffIdx || newStaffIdx === task.currentStaffIdx) continue;

  //     const newStaffName = cn(empByIndex.get(newStaffIdx), "staffname") || "";
  //     const currentUsernameNorm = userNorm(currentUsername); // drop domain, lower-case

  //     // CP/CM => NA service; SP/SM => task.service (single tag now)
  //     const service =
  //       task.role === "SP" || task.role === "SM" ? (task.service || "NA") : "NA";

  //     const KEY = keyify(task.clientCode, task.role, service, /* user being reassigned */ userNorm(task.currentUsername));

  //     entries.push({
  //       CLIENTCODE: task.clientCode,
  //       FROM_USER: currentUsernameNorm,
  //       KEY,
  //       ROLE_FOR_REASSIGNMENT: task.role,
  //       SERVICE: service,
  //       TO_USER: "sysadmin",
  //       UPDATED_TS: formatTimestampWithTz(new Date()),
  //       USER_FOR_REASSIGNMENT: userNorm(task.currentUsername),
  //       NEW_STAFF_SELECTION: newStaffName,
  //       WORKFLOW: WF.DRAFT_NEEDS_REVIEW,
  //     });
  //   }

  //   if (!entries.length) return;

  //   await Promise.all(entries.map(writeWorkflowEntry));
  //   // merge locally so admin columns update right away
  //   setMetadata(prev => [...prev, ...entries]);
  // }

  // Function to record draft reassignment acceptance or rejection by rprs committee
  async function recordAdminDecision(task, decision, reviewerUsername) {
    // decision is WF.DRAFT_ACCEPTED or WF.DRAFT_REJECTED
    const intent = decision === "accept" ? "admin_accept" : "admin_reject";
    const entry = await updateWorkflow({
      intent,
      task,
      actor: reviewerUsername,     // who clicked
      reviewer: reviewerUsername,  // used by updateWorkflow as FROM/TO
      empByIndex,
    });
    setMetadata(prev => prev.concat(entry));
  }



  // --- Reassignment Wizard filters (mini filterbar inside the tray)
  const [rwzFilters, setRwzFilters] = useState({ q: "", role: "All", status: "All" });


  // ---------------- Reassignment Wizard state ----------------
  const [wizardTab, setWizardTab] = useState("drafts"); // "drafts" | "summary"
  const [drafts, setDrafts] = useState(new Map());       // key: taskId, value: { newStaffIdx, task }
  // const [staffQuery, setStaffQuery] = useState("");
  // NEW: manual elective map: KEY -> true
  const [electiveMap, setElectiveMap] = useState(() => new Map());

  // Toggle an "elective" flag for a given client/role/service/staff combo
  const toggleElective = useCallback(
    ({ clientCode, role, service, staffIdx }) => {
      if (!clientCode || !role || staffIdx == null) return;

      const username = usernameByIndex(staffIdx);
      if (!username) return;

      // For CP/CM service should be "NA"
      const svc =
        role === "SP" || role === "SM"
          ? (service || "NA")
          : "NA";

      const key = keyify(clientCode, role, svc, username);

      setElectiveMap(prev => {
        const next = new Map(prev);
        if (next.has(key)) {
          // second click → remove
          next.delete(key);
        } else {
          // first click → add
          next.set(key, true);
        }
        return next;
      });
    },
    [usernameByIndex]
  );



  const handleDeleteTask = useCallback(async (task) => {
    const key = task.wfKey || task.key;
    if (!window.confirm(`Delete task for ${task.clientCode} (${task.role})? This cannot be undone.`)) return;

    // Optimistic: remove from UI immediately
    setMetadata(prev => prev.filter(w => cn(w, 'key', null) !== key));
    setElectiveMap(prev => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    setDrafts(prev => {
      if (!prev.has(task.id)) return prev;
      const next = new Map(prev);
      next.delete(task.id);
      return next;
    });

    // Fire the blob deletion
    try {
      await deleteTask(key);
    } catch (err) {
      console.error('[RPRS] Delete task blob failed:', err);
    }
  }, []);

  const handleBulkElectiveConfirm = useCallback((tasks) => {
    // Track which keys we expect to see in baseTasks before clearing loading
    const keys = new Set();
    for (const t of tasks) {
      const svc = (t.role === 'SP' || t.role === 'SM') ? (t.service || 'NA') : 'NA';
      keys.add(keyify(t.clientCode, t.role, svc, t.currentUsername));
    }
    pendingBulkKeysRef.current = keys;
    setWizardLoading(true);
    // Add each task to electiveMap so they appear in baseTasks / the wizard
    setElectiveMap(prev => {
      const next = new Map(prev);
      // Prune old terminal entries that aren't part of this batch
      for (const [k] of next) {
        if (keys.has(k)) continue;
        const existing = metadataByKey.get(k);
        if (existing) {
          const wf = cn(existing, 'workflow', null);
          if (wf === WF.DRAFT_ACCEPTED || wf === WF.DRAFT_REJECTED) {
            next.delete(k);
          }
        }
      }
      for (const t of tasks) {
        const svc = (t.role === 'SP' || t.role === 'SM') ? (t.service || 'NA') : 'NA';
        const key = keyify(t.clientCode, t.role, svc, t.currentUsername);
        next.set(key, true);
      }
      return next;
    });

    // Pre-populate drafts so the new staff is already selected when the wizard opens
    setDrafts(prev => {
      const next = new Map(prev);
      for (const t of tasks) {
        const svc = (t.role === 'SP' || t.role === 'SM') ? (t.service || 'NA') : 'NA';
        const taskId = `${t.clientCode}|${t.role}|${svc}`;
        // Always overwrite — user may be re-assigning with a different new staff selection
        const taskStub = {
          id: taskId,
          key: keyify(t.clientCode, t.role, svc, t.currentUsername),
          clientCode: t.clientCode,
          clientName: t.clientName,
          role: t.role,
          service: svc,
          currentStaffIdx: t.currentStaffIdx,
          currentStaffName: t.currentStaffName,
          currentUsername: t.currentUsername,
          contIndex: t.contIndex,
          billed: t.billed || 0,
          isElective: true,
        };
        next.set(taskId, { task: taskStub, newStaffIdx: t.newStaffIdx });
      }
      return next;
    });

    // Open wizard on the Drafts tab, filtered to ELECTIVE tasks
    setPanelOpen(true);
    setWizardTab('drafts');
    setRwzFilters(f => ({ ...f, q: '', role: 'All', status: WF.ELECTIVE }));
  }, [metadataByKey]);

  // Build a “task” list from current data (example heuristic: any inactive staff with managed $)
  // const ROLE_FIELDS = [
  //   { role: "CP", nameField: "CLIENTPARTNER",    indexField: "CPINDEX" },
  //   { role: "CM", nameField: "CLIENTMANAGER",    indexField: "CMINDEX" },
  //   { role: "SP", nameField: "SERVICEPARTNER",   indexField: "SPINDEX" },
  //   { role: "SM", nameField: "SERVICEMANAGER",   indexField: "SMINDEX" },
  // ];
  // Use canonical field names; cn() will match any casing/underscores
  const ROLE_FIELDS = [
    { role: "CP", nameField: "clientpartner",  indexField: "cpindex" },
    { role: "CM", nameField: "clientmanager",  indexField: "cmindex" },
    { role: "SP", nameField: "servicepartner", indexField: "spindex" },
    { role: "SM", nameField: "servicemanager", indexField: "smindex" },
  ];

  // helper: return single service tag for row/role or "NA"
  const serviceForRowRole = (r, role) =>
    role === "SP" || role === "SM" ? (getServiceTag(r) || "NA") : "NA";

  // Map: "CLIENTCODE|ROLE|SERVICE" -> the workflow tasks that already exist for that seat.
  // The Excel import takes the current holder from PE, so an open task here under anyone
  // else is stale (the seat moved outside RPRS) and gets flagged rather than filled in.
  const metaHoldersByCrs = useMemo(() => {
    const m = new Map();

    // metadataByKey is already deduped to one row per (seat, holder), newest status wins.
    for (const w of metadataByKey.values()) {
      const clientCode = normalizeClientCode(cn(w, "clientcode", ""));
      const role = String(cn(w, "role_for_reassignment", "")).trim().toUpperCase();
      const service = normalizeServindex(cn(w, "service", "")) || "NA";
      const username = userNorm(cn(w, "user_for_reassignment", ""));
      if (!clientCode || !role || !username) continue;

      const crs = `${clientCode}|${role}|${service}`;
      if (!m.has(crs)) m.set(crs, []);
      m.get(crs).push({ username, status: cn(w, "workflow", null) });
    }

    return m;
  }, [metadataByKey]);


  const baseTasks = useMemo(() => {
    const byKey = new Map();

    for (const r of detail) {
      const billedAmt = Number(cn(r, "billed") || 0);
      if (!billedAmt) continue;

      for (const F of ROLE_FIELDS) {

        const staffIx = resolveIndex(r, F.role, nameToIndex);

        if (!Number.isFinite(staffIx)) continue;

        const clientCode = cn(r, "clientcode");
        const clientName = cn(r, "clientname") || "";
        const contIndex = cn(r, "contindex");
        // const service =
        //   (F.role === "SP" || F.role === "SM")
        //     ? (getServiceTag(r) || "NA")
        //     : "NA";
        const service = serviceForRowRole(r, F.role);
        const user = usernameByIndex(staffIx);
        // const key = `${clientCode}|${F.role}|${service}`;
        const key = keyify(clientCode, F.role, service, user)

        const prev = byKey.get(key) || {
          contIndex,
          clientCode,
          clientName,
          role: F.role,
          service,
          staffIdx: staffIx,
          staffName:
            cn(empByIndex.get(staffIx) || {}, "staffname") ||
            cn(r, F.nameField) ||
            "(Unknown)",
          currentUsername: user,
          billed: 0,
        };

        prev.billed += billedAmt;
        // prefer the explicit staff name from roster if available
        prev.staffIdx = staffIx;

        prev.staffName = cn(empByIndex.get(staffIx) || {}, "staffname") || prev.staffName;
        byKey.set(key, prev);
      }
    }

    // 2) emit tasks only for items whose current holder is inactive
    const tasks = [];
    // for (const t of byClientRole.values()) {
    for (const t of byKey.values()) {
      const emp = empByIndex.get(t.staffIdx);
      const key = keyify(t);
      const isAuto = isInactiveEmp(emp);
      const isElective = electiveMap.has(key);

      if (!isAuto && !isElective) continue;

      //TODO going to use above logic to avoid pulling in excel import accepted ones, think I need to set the imports as electives with setElectiveMap upon seeding
      // // If metadata exists for this KEY, keep it visible even if the current holder is active.
      // // This is required for Elective + Excel Import tasks so they don't disappear after refresh / submit.
      const wf = metadataByKey.get(key) || null;
      const wfStatus = cn(wf, "workflow", null);
      const hasMeta = !!wfStatus;

      // if (!isAuto && !isElective && !hasMeta) continue;

      // if (isInactiveEmp(emp)) {

      tasks.push({
        id: `${t.clientCode}|${t.role}|${t.service}`,
        key: key,
        contIndex: t.contIndex,
        clientCode: t.clientCode,
        clientName: t.clientName,
        role: t.role,                 // CP | CM | SP | SM
        service: t.service,         // <- single service string
        currentStaffIdx: t.staffIdx,
        currentStaffName: t.staffName,
        currentUsername: usernameByIndex(t.staffIdx) || null,
        billed: t.billed,              // <- we’ll redistribute this amount
        // isElective
        isElective: isElective || wfStatus === WF.ELECTIVE || wfStatus === WF.EXCEL_IMPORT,
        isImported: wfStatus === WF.EXCEL_IMPORT,
      });
      // }
    }

    // Inject bulk elective tasks that have zero WIP billed (absent from detail).
    // handleBulkElectiveConfirm and the Excel import seed these into electiveMap + drafts,
    // but the main detail loop above skips them because billedAmt === 0.
    const emittedKeys = new Set(tasks.map(t => t.key));
    for (const [, { task }] of drafts.entries()) {
      if (!task.isElective) continue;
      if (emittedKeys.has(task.key)) continue;  // dedup by key (includes username), not taskId
      if (!electiveMap.has(task.key)) continue;
      tasks.push({
        id: task.id,
        key: task.key,
        contIndex: task.contIndex,
        clientCode: task.clientCode,
        clientName: task.clientName,
        role: task.role,
        service: task.service,
        currentStaffIdx: task.currentStaffIdx,
        currentStaffName: task.currentStaffName,
        currentUsername: task.currentUsername,
        billed: 0,
        isElective: true,
        isImported: false,
      });
    }

    return tasks;
  // }, [detail, nameToIndex, empByIndex, nameToUser, usernameByIndex, electiveMap]);
  }, [detail, nameToIndex, empByIndex, nameToUser, usernameByIndex, electiveMap, metadataByKey, drafts]);


  //Debugging base tasks
  useEffect(() => {
    console.log("[RPRS] baseTasks:", baseTasks.length);
    console.log("[RPRS] baseTasks key sample:", baseTasks.slice(0, 10).map(t => t.key));
  }, [baseTasks]);

  // Clear wizardLoading once ALL pending bulk-elective keys appear in baseTasks
  useEffect(() => {
    if (!wizardLoading) return;
    const pending = pendingBulkKeysRef.current;
    if (pending.size === 0) { setWizardLoading(false); return; }
    // Check both baseTasks and draft stubs — detail data may be stale after a prior submit
    const knownKeys = new Set(baseTasks.map(t => t.key));
    for (const [, { task }] of drafts) {
      if (task?.key) knownKeys.add(task.key);
    }
    for (const k of pending) {
      if (!knownKeys.has(k)) return; // still waiting
    }
    pendingBulkKeysRef.current = new Set();
    setWizardLoading(false);
  }, [baseTasks, wizardLoading, drafts]);

  // ---------------- Excel Template Download ----------------
  const onDownloadImportTemplate = useCallback(() => {
    try {
      if (loading) {
        alert("Still loading data — please try again in a moment.");
        return;
      }
      if (!empByIndex?.size) {
        alert("Employee list not loaded yet.");
        return;
      }
      if (!detail?.length) {
        alert("Client detail not loaded yet.");
        return;
      }

      // Active employees only (prevents most typos / invalid picks)
      const employees = [...empByIndex.entries()]
        .map(([idx, e]) => ({
          staffIndex: Number(idx),
          staffName: cn(e, "staffname", ""),
          staffUser: cn(e, "staffuser", ""),
          active: !isInactiveEmp(e),
        }))
        .filter((e) => Number.isFinite(e.staffIndex) && e.staffName && e.active)
        .sort((a, b) => String(a.staffName).localeCompare(String(b.staffName)));

      // Client + service list (for reference / optional helper lookups)
      const clients = [];
      const seen = new Set();

      for (const r of detail) {
        const clientCode = cn(r, "clientcode");
        if (!clientCode) continue;

        const clientName = cn(r, "clientname") || "";
        const svc = getServiceTag(r) || "NA";
        const k = `${clientCode}|${svc}`;
        if (seen.has(k)) continue;
        seen.add(k);

        clients.push({ clientCode, clientName, servindex: svc });
      }

      clients.sort(
        (a, b) =>
          String(a.clientCode).localeCompare(String(b.clientCode)) ||
          String(a.servindex || "").localeCompare(String(b.servindex || ""))
      );

      const wb = buildRprsTemplateWorkbook({ employees, clients });

      const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      downloadWorkbook(wb, `rprs_bulk_reassign_template_${ymd}.xlsx`);
    } catch (err) {
      console.error("[RPRS] template download failed", err);
      alert("Template download failed — see console for details.");
    }
  }, [empByIndex, detail, loading]);

  // ---------------- Excel Import -> Seed workflow metadata ----------------
  const importRowsToTasks = useCallback(
    async (file) => {
      if (!file) return;

      setImportBusy(true);
      setImportSummary(null);

      try {
        if (loading) {
          alert("Still loading data — please try again in a moment.");
          return;
        }
        if (!empByIndex?.size) {
          alert("Employee list not loaded yet.");
          return;
        }

        const { rows } = await parseRprsImportFile(file);

        if (!rows?.length) {
          alert("No rows found in uploaded file.");
          return;
        }

        // { rowNum, kind, reason, detail } — "skip" rows are dropped, "note" rows still import.
        // reason is the group label in the summary, so row-specific values go in detail.
        const rowProblems = [];
        const skip = (rowNum, reason, detail) => rowProblems.push({ rowNum, kind: "skip", reason, detail });
        const note = (rowNum, reason, detail) => rowProblems.push({ rowNum, kind: "note", reason, detail });
        const seatLabel = (clientCode, role, service) =>
          service === "NA" ? `${role} ${clientCode}` : `${role} ${clientCode} (${service})`;

        const plannedByKey = new Map(); // KEY -> { task, newStaffIdx, rowNum, source }

        const pickFromRow = (row, role) => {
          // Optional index override columns (if you add helper formula columns in the template)
          const idxCol =
            role === "CP" ? "new_cp_idx" :
            role === "CM" ? "new_cm_idx" :
            role === "SP" ? "new_sp_idx" :
                            "new_sm_idx";

          const nameCol =
            role === "CP" ? "new_cp" :
            role === "CM" ? "new_cm" :
            role === "SP" ? "new_sp" :
                            "new_sm";

          const rawIdx = cn(row, idxCol, "");
          const idxNum = rawIdx === "" ? null : Number(rawIdx);

          if (idxNum != null && Number.isFinite(idxNum) && idxNum > 0) {
            // Index override (optional). Requires that the index exists in the roster so the UI can display it.
            const staffRow = empByIndex.get(idxNum);
            if (!staffRow) return { error: "Unknown STAFFINDEX", value: idxNum };
            return { newStaffIdx: idxNum, newStaffName: cn(staffRow, "staffname", "") || null, source: "idx" };
          }

          const rawName = String(cn(row, nameCol, "") || "").trim();
          if (!rawName) return null;

          const resolvedIdx = nameToIndex.get(normStrong(rawName));
          if (!Number.isFinite(resolvedIdx)) {
            return { error: "Unknown employee name", value: rawName };
          }
          return { newStaffIdx: resolvedIdx, newStaffName: rawName, source: "name" };
        };

        // Who holds each seat today, straight from PE's client + service tables (live, via
        // the active_services LA case) for just the clients in this file. Not `detail`: it
        // only carries services billed in the last 12 months, so a client billed once a year
        // drops out of it right before its next bill and its seats can't be resolved.
        const fileCodes = [...new Set(rows.map((r) => normalizeClientCode(cn(r, "clientcode", ""))))]
          .filter(isSafeClientCode);

        const codeChunks = [];
        for (let i = 0; i < fileCodes.length; i += 200) codeChunks.push(fileCodes.slice(i, i + 200));

        const lookups = await runPool(codeChunks, 2, (codes) => fetchActiveServices(codes));
        const lookupFailed = lookups.find((l) => !l?.ok);

        if (lookupFailed) {
          throw new Error(`Couldn't look up current assignments in PE — ${lookupFailed.error?.message || lookupFailed.error}`);
        }

        // "CLIENTCODE|ROLE|SERVICE" -> PE's current holder. One row per client-service, so
        // SP/SM map 1:1; CP/CM repeat on every row of the client and sum its billed.
        const holderByCrs = new Map();
        const activeClients = new Set();

        for (const r of lookups.flatMap((l) => l.value || [])) {
          const clientCode = normalizeClientCode(cn(r, "clientcode", ""));
          if (!clientCode) continue;
          activeClients.add(clientCode);

          const billed = Number(cn(r, "billed") || 0) || 0;

          for (const F of ROLE_FIELDS) {
            const staffIdx = resolveIndex(r, F.role, nameToIndex);
            if (!Number.isFinite(staffIdx)) continue;

            const crs = `${clientCode}|${F.role}|${serviceForRowRole(r, F.role)}`;
            const prev = holderByCrs.get(crs);

            if (prev) {
              prev.billed += billed;
              continue;
            }

            holderByCrs.set(crs, {
              contIndex: cn(r, "contindex", null),
              clientName: cn(r, "clientname", "") || "",
              staffIdx,
              staffName: cn(empByIndex.get(staffIdx) || {}, "staffname", "") || cn(r, F.nameField, "") || "",
              billed,
            });
          }
        }

        // Build tasks (1 per role reassignment)
        rows.forEach((r, i) => {
          const rowNum = i + 2; // if header row is row 1 in Excel
          const clientCode = normalizeClientCode(cn(r, "clientcode", ""));
          const servindex = normalizeServindex(cn(r, "servindex", ""));

          if (!clientCode) {
            skip(rowNum, "Missing CLIENTCODE", "");
            return;
          }
          if (!isSafeClientCode(clientCode)) {
            skip(rowNum, "CLIENTCODE has unexpected characters", clientCode);
            return;
          }

          const roles = ["CP", "CM", "SP", "SM"];
          for (const role of roles) {
            const pick = pickFromRow(r, role);
            if (!pick) continue;

            if (pick.error) {
              skip(rowNum, pick.error, `${role}: ${pick.value}`);
              continue;
            }

            const service =
              role === "SP" || role === "SM"
                ? servindex
                : "NA";

            if ((role === "SP" || role === "SM") && (!service || service === "NA")) {
              skip(rowNum, "SERVINDEX is required when changing SP/SM", `${role} ${clientCode}`);
              continue;
            }

            const seat = seatLabel(clientCode, role, service);
            const crs = `${clientCode}|${role}|${service}`;
            const holder = holderByCrs.get(crs);

            if (!holder) {
              if (!activeClients.has(clientCode)) {
                skip(rowNum, "Not an active client in PE (LOST, inactive, or unknown code)", seat);
              } else if (role === "SP" || role === "SM") {
                skip(rowNum, "Client is active but doesn't have this service in PE", seat);
              } else {
                skip(rowNum, "No current holder for this role in PE", seat);
              }
              continue;
            }

            const currentUsername = usernameByIndex(holder.staffIdx);

            if (!currentUsername) {
              skip(rowNum, "Current holder isn't in the RPRS staff list", `${seat} — ${holder.staffName || `STAFFINDEX ${holder.staffIdx}`}`);
              continue;
            }

            // Nothing to reassign — the pick already holds the seat.
            if (Number(pick.newStaffIdx) === Number(holder.staffIdx)) {
              skip(rowNum, "Already holds the seat — no change", `${seat} — ${holder.staffName || currentUsername}`);
              continue;
            }

            const KEY = keyify(clientCode, role, service, currentUsername);

            // A task already on screen gets its pick filled in — the entry write supersedes
            // the old row under the same TaskKey, so no duplicate task appears. Only tasks
            // whose pick is locked (with the committee, or already accepted) are left alone.
            const existing = metadataByKey.get(KEY);
            const existingStatus = cn(existing || {}, "workflow", null);

            if (existing && existingStatus && !IMPORT_OVERWRITABLE.has(existingStatus)) {
              skip(rowNum, "Task is with the committee or already accepted — left untouched", `${seat} — ${existingStatus}`);
              continue;
            }

            // PE decides the holder. A still-open task for this seat under someone else is
            // stale (the seat moved outside RPRS) — leave it be, but say so.
            const stale = (metaHoldersByCrs.get(crs) || []).filter(
              (h) => h.username !== currentUsername && h.status !== WF.DRAFT_ACCEPTED && h.status !== WF.DRAFT_REJECTED
            );

            if (stale.length) {
              note(rowNum, "Open task exists under someone PE no longer shows as the holder — left alone", `${seat} — ${stale.map((h) => h.username).join(", ")}`);
            }

            // Last row wins for duplicate KEYs inside the same file. CP/CM collapse to
            // service "NA", so two rows for one client can land on the same task — say so
            // when the picks disagree instead of silently dropping the earlier one.
            const clash = plannedByKey.get(KEY);

            if (clash && Number(clash.newStaffIdx) !== Number(pick.newStaffIdx)) {
              note(rowNum, "Overrides an earlier row for the same task — last row wins", `${seat} — overrides row ${clash.rowNum}`);
            }

            plannedByKey.set(KEY, {
              rowNum,
              newStaffIdx: pick.newStaffIdx,
              source: pick.source,
              task: {
                id: `${clientCode}|${role}|${service}`,
                key: KEY,
                contIndex: holder.contIndex,
                clientCode,
                clientName: holder.clientName,
                role,
                service,
                currentStaffIdx: holder.staffIdx,
                currentStaffName: holder.staffName,
                currentUsername,
                billed: holder.billed,
                // existing wf status (if any)
                wfStatus: existingStatus,
                // Holders are usually still active and many seats have no recent billing, so
                // the wizard only shows these via electiveMap + the drafts injection in baseTasks.
                isElective: true,
              },
            });
          }
        });

        const planned = [...plannedByKey.values()];

        console.log("[RPRS] import:", { rows: rows.length, clientsLookedUp: fileCodes.length, planned: planned.length, rowProblems });

        if (!planned.length) {
          alert("No valid role changes were found.\n\n" + formatImportProblems(rowProblems));
          setImportSummary({
            parsedRows: rows.length,
            plannedTasks: 0,
            written: 0,
            rowProblems: rowProblems.length,
            writeFailures: 0,
          });
          return;
        }

        // Write seeded entries with limited concurrency (avoid overwhelming Logic Apps)
        const concurrency = 6;

        const results = await runPool(planned, concurrency, async (p) => {
          const entry = await updateWorkflow({
            intent: "excel_import",
            task: p.task,
            newStaffIdx: p.newStaffIdx,
            actor: CURRENT_USER,
            routeTo: CURRENT_USER,
            empByIndex,
            isAdmin,
          });
          return { entry, planned: p };
        });

        const successes = [];
        const failures = [];

        results.forEach((r) => {
          if (r?.ok) successes.push(r.value);
          else failures.push(r.error);
        });

        if (failures.length) console.error("[RPRS] import write failures:", failures);

        const written = successes.filter((s) => s.entry);

        // Update metadata in-memory so the wizard reflects immediately (no reload)
        if (written.length) {
          setMetadata((prev) => prev.concat(written.map((s) => s.entry)));
        }

        // Register the written tasks the way Bulk Elective does: electiveMap makes baseTasks
        // emit seats whose holder is still active, and the drafts entry carries the pick to
        // Summary/Confirm and injects seats with no recent billing (absent from detail).
        setElectiveMap((prev) => {
          const next = new Map(prev);
          for (const s of written) next.set(s.planned.task.key, true);
          return next;
        });

        // The file's pick replaces any earlier draft for the seat — it's what was just saved.
        setDrafts((prev) => {
          const next = new Map(prev);
          for (const s of written) {
            next.set(s.planned.task.id, { task: s.planned.task, newStaffIdx: s.planned.newStaffIdx });
          }
          return next;
        });

        // Open wizard + filter to imported tasks
        setWizardTab("drafts");
        setPanelOpen(true);
        setRwzFilters((f) => ({ ...f, q: "", role: "All", status: WF.EXCEL_IMPORT }));

        const summary = {
          parsedRows: rows.length,
          plannedTasks: planned.length,
          written: written.length,
          rowProblems: rowProblems.length,
          writeFailures: failures.length,
        };

        setImportSummary(summary);

        const problemsText = formatImportProblems(rowProblems);
        const msg =
          `Excel import complete.\n\n` +
          `Rows parsed: ${summary.parsedRows}\n` +
          `Tasks planned: ${summary.plannedTasks}\n` +
          `Tasks written: ${summary.written}\n` +
          `Write failures: ${summary.writeFailures}` +
          (problemsText ? `\n\n${problemsText}` : "");

        alert(msg);

      } catch (err) {
        console.error("[RPRS] import failed", err);
        alert(`Import failed: ${err?.message || err}`);
      } finally {
        setImportBusy(false);
      }
    },
    [
      metaHoldersByCrs,
      empByIndex,
      isAdmin,
      metadataByKey,
      nameToIndex,
      usernameByIndex,
      loading,
    ]
  );


  const reassignmentTasks = useMemo(() => {
    return baseTasks.map(t => {
      const wf = metadataByKey.get(t.key) || null;

      const pendingIdxFromMeta =
        Number(cn(wf, "new_staff_selection_idx", null));
      const pendingIdxResolved =
        Number.isFinite(pendingIdxFromMeta)
          ? pendingIdxFromMeta
          : (nameToIndex.get(normStrong(cn(wf, "new_staff_selection", ""))) ?? null);

      return {
        // --- admin metadata (nullable if not found) ---
        ...t,
        wfKey: t.key,
        wfFrom:    cn(wf, "from_user", null),
        wfTo:      cn(wf, "to_user", null),
        wfUpdated: cn(wf, "updated_ts", null),
        wfStatus:  cn(wf, "workflow", null),
        wfPendingStaff:  cn(wf, "new_staff_selection", null),
        wfPendingIdx:    pendingIdxResolved,
      };
    });
  }, [baseTasks, metadataByKey]);

  // Non-admins see only tasks routed to them (wfTo = TO_USER in metadata).
  // For tasks with no metadata yet, fall back to the computed routeToFor() owner.
  const wizardTasks = isAdmin
    ? reassignmentTasks
    : reassignmentTasks.filter(t => {
        const assignedTo = t.wfTo || routeToFor(t);
        return assignedTo === CURRENT_USER;
      });

  const staffWithElectiveTasks = useMemo(() => {
    const s = new Set();
    for (const t of reassignmentTasks) {
      if (t.isElective) {
        s.add(t.currentStaffIdx);
      }
    }
    return s;
  }, [reassignmentTasks]);


  // Hydrate Draft selections from Excel-imported metadata so the user can immediately
  // review + Confirm (Summary step uses `drafts`, not just wfPendingIdx).
  useEffect(() => {
    if (!panelOpen) return;

    setDrafts((prev) => {
      const next = new Map(prev);
      let changed = false;

      for (const t of reassignmentTasks) {
        if (t.wfStatus !== WF.EXCEL_IMPORT) continue;
        if (!Number.isFinite(Number(t.wfPendingIdx))) continue;

        if (!next.has(t.id)) {
          next.set(t.id, { task: t, newStaffIdx: Number(t.wfPendingIdx) });
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [panelOpen, reassignmentTasks]);


  // const reassignmentTasks = useMemo(() => {
  //   // 1) roll up billed by (clientCode, role)
  //   // const byClientRole = new Map(); // key: `${client}|${role}` -> { billed, staffIdx, staffName, clientName }
  //   const byKey = new Map();        // `${client}|${role}|${service}` -> {...}

  //   for (const r of detail) {
  //     // const billedAmt = Number(r.BILLED || 0);
  //     const billedAmt = Number(cn(r, "billed") || 0);
  //     if (!billedAmt) continue;

  //     for (const F of ROLE_FIELDS) {

  //       const staffIx = resolveIndex(r, F.role, nameToIndex);

  //       if (!Number.isFinite(staffIx)) continue;

  //       const clientCode = cn(r, "clientcode");
  //       const clientName = cn(r, "clientname") || "";
  //       const service =
  //         (F.role === "SP" || F.role === "SM")
  //           ? (getServiceTag(r) || "NA")
  //           : "NA";
  //       const user = usernameByIndex(staffIx);
  //       // const key = `${clientCode}|${F.role}|${service}`;
  //       const key = keyify(clientCode, F.role, service, user)

  //       const prev = byKey.get(key) || {
  //         clientCode,
  //         clientName,
  //         role: F.role,
  //         service,
  //         staffIdx: staffIx,
  //         staffName:
  //           cn(empByIndex.get(staffIx) || {}, "staffname") ||
  //           cn(r, F.nameField) ||
  //           "(Unknown)",
  //         currentUsername: user,
  //         billed: 0,
  //       };

  //       prev.billed += billedAmt;
  //       // prefer the explicit staff name from roster if available
  //       prev.staffIdx = staffIx;

  //       prev.staffName = cn(empByIndex.get(staffIx) || {}, "staffname") || prev.staffName;
  //       byKey.set(key, prev);
  //     }
  //   }

  //   // 2) emit tasks only for items whose current holder is inactive
  //   const tasks = [];
  //   // for (const t of byClientRole.values()) {
  //   for (const t of byKey.values()) {
  //     const emp = empByIndex.get(t.staffIdx);

  //     if (isInactiveEmp(emp)) {
  //       // Build canonical workflow key & lookup metadata
  //       // const wfKey = buildNormMetaKey(t.clientCode, t.role, t.service, t.staffName);
  //       const wfKey = keyify(t)
  //       const wf = metadataByKey.get(wfKey) || null;
  //       if (!wf && process.env.NODE_ENV !== "production") {
  //         console.debug("Missing metadata for key", wfKey);
  //       }
  //       if (!wf) {
  //         console.log("Missing metadata for key", wfKey); // always visible
  //       }

  //       tasks.push({
  //         id: `${t.clientCode}|${t.role}|${t.service}`,
  //         key: keyify(t),
  //         clientCode: t.clientCode,
  //         clientName: t.clientName,
  //         role: t.role,                 // CP | CM | SP | SM
  //         service: t.service,         // <- single service string
  //         currentStaffIdx: t.staffIdx,
  //         currentStaffName: t.staffName,
  //         currentUsername: usernameByIndex(t.staffIdx) || null,
  //         billed: t.billed,              // <- we’ll redistribute this amount

  //         // --- admin metadata (nullable if not found) ---
  //         wfKey,
  //         wfFrom:    cn(wf, "from_user", null),
  //         wfTo:      cn(wf, "to_user", null),
  //         wfUpdated: cn(wf, "updated_ts", null),
  //         wfStatus:  cn(wf, "workflow", null),
  //         wfPendingStaff:  cn(wf, "new_staff_selection", null),
  //       });
  //     }
  //   }

  //   return tasks;
  // }, [detail, nameToIndex, empByIndex, metadataByKey, nameToUser]);

  // // Given a task and a selected newStaffIdx, build the final JSON row
  // function buildWorkflowJson({ task, newStaffIdx }) {
  //   // Username for the employee being reassigned ("USER_FOR_REASSIGNMENT")
  //   const currentUsername =
  //     nameToUser.get(normStrong(task.currentStaffName)) || "unknownuser";

  //   // Pick a service tag for SP/SM; CP/CM use "NA"
  //   // const service =
  //   //   task.role === "SP" || task.role === "SM"
  //   //     ? (task.serviceTags?.[0] || "NA")
  //   //     : "NA";
  //   const service = (task.role === "SP" || task.role === "SM") ? (task.service || "NA") : "NA";

  //   const KEY = keyify(task.clientCode, task.role, service, currentUsername);

  //   // Full name of the new selection (what you asked to capture)
  //   // const newStaffName = empByIndex.get(newStaffIdx)?.STAFFNAME || "";
  //   const newStaffName = cn(empByIndex.get(newStaffIdx) || {}, "staffname") || "";

  //   return {
  //     CLIENTCODE: task.clientCode,
  //     FROM_USER: task.wfFrom ?? null,
  //     KEY,
  //     ROLE_FOR_REASSIGNMENT: task.role,      // "CP" | "CM" | "SP" | "SM"
  //     SERVICE: service,                       // e.g., "INDTAX" or "NA"
  //     TO_USER: task.wfTo ?? null,
  //     UPDATED_TS: formatTimestampWithTz(new Date()),
  //     USER_FOR_REASSIGNMENT: currentUsername, // username of the person being reassigned
  //     NEW_STAFF_SELECTION: newStaffName,      // full name from roster
  //     WORKFLOW: task.wfStatus ?? "To be reassigned",
  //   };
  // }

  // For bulk reassignment
  const filteredTasksForBulk = useMemo(() => {
    const sorted = [...reassignmentTasks].sort((a, b) =>
      String(a.clientCode).localeCompare(String(b.clientCode))
    );

    const q = (rwzFilters.q || "").toLowerCase().trim();

    return sorted.filter((t) => {
      if (rwzFilters.role !== "All" && t.role !== rwzFilters.role) return false;

      if (rwzFilters.status && rwzFilters.status !== "All") {
        const effectiveStatus = t.wfStatus || "To be reassigned";
        if (effectiveStatus !== rwzFilters.status) return false;
      }

      if (!q) return true;
      const hay = `${t.clientCode} ${t.clientName} ${t.currentStaffName}`.toLowerCase();
      return hay.includes(q);
    });
  }, [reassignmentTasks, rwzFilters]);

  const applyBulk = (pickedIdx) => {
    // clicked same name again → StaffSelect will send null (see togglePick)
    // :contentReference[oaicite:3]{index=3}
    if (pickedIdx == null) {
      setDrafts(prev => {
        const next = new Map(prev);
        filteredTasksForBulk.forEach(t => {
          const locked = t.wfStatus === WF.DRAFT_NEEDS_REVIEW || t.wfStatus === WF.DRAFT_ACCEPTED;
          if (!locked) next.delete(t.id);
        });
        return next;
      });
      setBulkIdx(null);
      return;
    }

    setDrafts(prev => {
      const next = new Map(prev);
      filteredTasksForBulk.forEach(t => {
        const locked = t.wfStatus === WF.DRAFT_NEEDS_REVIEW || t.wfStatus === WF.DRAFT_ACCEPTED;
        if (locked) return;
        next.set(t.id, { task: t, newStaffIdx: pickedIdx });
      });
      return next;
    });

    setBulkIdx(pickedIdx);
  };

  const clearAllSelections = () => {
    setDrafts(new Map()); // clears ALL editable picks (regardless of filters)
    setBulkIdx(null);
  };


  /* ---------- sorting helpers moved outside component for stable reference ----------- */

  /* ---------------- employee→client detail for EXPANSION ---------------- */
  const buildClientRowsFor = (empIdx) => {
    const byClient = new Map(); // key = CLIENTCODE

    const ensure = (code, name) => {
      if (!byClient.has(code)) {
        byClient.set(code, {
          code,
          name,
          // store chip keys in a Set so duplicates are removed automatically
          _chipKeys: new Set(), // e.g., "CP|", "CM|", "SP|MAS", "SM|BUSTAX"
          amounts: { cp: 0, cm: 0, sp: 0, sm: 0 },
        });
      }
      return byClient.get(code);
    };

    for (const r of detail) {
      // const code = r.CLIENTCODE;
      // const name = r.CLIENTNAME || r.CLIENT || "";
      // const billedAmt = num(r.BILLED);
      // const servIndex = (r.SERVINDEX || "").toString().trim(); // e.g., "MAS", "BUSTAX"
      const code = cn(r, "clientcode");
      const name = cn(r, "clientname") || cn(r, "client") || "";
      const billedAmt = num(cn(r, "billed") || 0);
      const servIndex = String(cn(r, "servindex", "")).trim(); // e.g., "MAS", "BUSTAX"

      const idxCP = resolveIndex(r, "CP", nameToIndex);
      const idxCM = resolveIndex(r, "CM", nameToIndex);
      const idxSP = resolveIndex(r, "SP", nameToIndex);
      const idxSM = resolveIndex(r, "SM", nameToIndex);

      if (idxCP === empIdx) {
        const x = ensure(code, name);
        x._chipKeys.add("CP|");         // no tag for CP
        x.amounts.cp += billedAmt;
      }
      if (idxCM === empIdx) {
        const x = ensure(code, name);
        x._chipKeys.add("CM|");         // no tag for CM
        x.amounts.cm += billedAmt;
      }
      if (idxSP === empIdx) {
        const x = ensure(code, name);
        x._chipKeys.add(`SP|${servIndex}`);  // tag for SP
        x.amounts.sp += billedAmt;
      }
      if (idxSM === empIdx) {
        const x = ensure(code, name);
        x._chipKeys.add(`SM|${servIndex}`);  // tag for SM
        x.amounts.sm += billedAmt;
      }
    }

    // finalize -> build display-friendly role chip objects + total
    const arr = [...byClient.values()].map(row => {
      const chips = [];
      const label = { CP: "Client Partner", CM: "Client Manager", SP: "Service Partner", SM: "Service Manager" };

      for (const key of row._chipKeys) {
        const [kind, tag] = key.split("|"); // kind in {CP,CM,SP,SM}
        const text = (kind === "SP" || kind === "SM") && tag
          ? `${label[kind]}: ${tag}`
          : label[kind];
        chips.push({ kind, tag: tag || "", text });
      }

      return {
        code: row.code,
        name: row.name,
        roleChips: chips.sort((a,b) =>
          a.kind.localeCompare(b.kind) || a.text.localeCompare(b.text)
        ),
        amounts: {
          ...row.amounts,
          total: row.amounts.cp + row.amounts.cm + row.amounts.sp + row.amounts.sm,
        },
      };
    });

    // arr.sort((a, b) => (b.amounts.total ?? 0) - (a.amounts.total ?? 0)); //sorting expansion by total billings
    arr.sort((a, b) => String(a.code).localeCompare(String(b.code))); // sorting expansion by client code
    return arr;
  };

  // CSV of RAW detail rows for the selected employee (no aggregation)
  const downloadCsvFor = (empIdx) => {
    // Keep only the raw detail rows where this employee appears in ANY role
    const rows = detail.filter(r => {
      const cp = resolveIndex(r, "CP", nameToIndex);
      const cm = resolveIndex(r, "CM", nameToIndex);
      const sp = resolveIndex(r, "SP", nameToIndex);
      const sm = resolveIndex(r, "SM", nameToIndex);
      return cp === empIdx || cm === empIdx || sp === empIdx || sm === empIdx;
    });

    // Column order (exactly as requested)
    const COLS = [
      "CLIENTCODE",
      "CLIENTNAME",
      "CLIENTORIGINATOR",
      "CLIENTPARTNER",
      "CLIENTMANAGER",
      "SERVICEPARTNER",
      "SERVICEMANAGER",
      "NICHE",
      "SERVINDEX",
      "WIPHOURS",
      "WIP",
      "BILLED",
    ];

    // CSV helpers
    const escape = (val) => {
      const v = (val ?? "").toString();
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };

    // Preserve leading zeros in CLIENTCODE for Excel (04776-001)
    const excelTextCell = (s) => {
      const v = (s ?? "").toString();
      // If it looks like it could lose a leading 0 or be coerced, force Excel to treat as text
      return /^0\d|^[0-9-]+$/.test(v) ? `="${v}"` : escape(v);
    };

    // Optional: stable sort (by CLIENTCODE, then SERVINDEX)
    rows.sort((a, b) =>
      // (a.CLIENTCODE || "").localeCompare(b.CLIENTCODE || "") ||
      // (a.SERVINDEX || "").localeCompare(b.SERVINDEX || "")
      (cn(a, "clientcode") || "").localeCompare(cn(b, "clientcode") || "") ||
      (cn(a, "servindex") || "").localeCompare(cn(b, "servindex") || "")
    );

    const header = COLS.join(",");
    const lines = rows.map(r => [
      excelTextCell(cn(r, "clientcode")),
      escape(cn(r, "clientname")),
      escape(cn(r, "clientoriginator")),
      escape(cn(r, "clientpartner")),
      escape(cn(r, "clientmanager")),
      escape(cn(r, "servicepartner")),
      escape(cn(r, "servicemanager")),
      escape(cn(r, "niche")),
      escape(cn(r, "servindex")),
      cn(r, "wiphours", ""),
      cn(r, "wip", ""),
      cn(r, "billed", ""),
      // excelTextCell(r.CLIENTCODE),
      // escape(r.CLIENTNAME),
      // escape(r.CLIENTORIGINATOR),
      // escape(r.CLIENTPARTNER),
      // escape(r.CLIENTMANAGER),
      // escape(r.NICHE),
      // escape(r.SERVINDEX),
      // r.WIPHOURS ?? "",
      // r.WIP ?? "",
      // r.BILLED ?? "",
    ].join(","));

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "employee_detail_raw.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ---------------- table bits ---------------- */
  const columns = useMemo(() => [
    { name: "Name", selector: r => r.name, sortable: true, grow: 2,
      sortFunction: sortWithInactiveTop(r => r.name, true) },
    { name: "Level", selector: r => r.level, sortable: true, width: "140px",
      sortFunction: sortWithInactiveTop(r => r.level, true) },
    { name: "Department", selector: r => r.dept, sortable: true, width: "140px",
      sortFunction: sortWithInactiveTop(r => r.dept, true) },
    { name: "Office", selector: r => r.office, sortable: true, width: "110px",
      sortFunction: sortWithInactiveTop(r => r.office, true) },

    // Four role columns (under the band)
    { name: "Client Partner", selector: r => r.cp, sortable: true, right: true,
      cell: r => <span>{money(r.cp)}</span>,
      sortFunction: sortWithInactiveTop(r => r.cp) },
    { name: "Client Manager", selector: r => r.cm, sortable: true, right: true,
      cell: r => <span>{money(r.cm)}</span>,
      sortFunction: sortWithInactiveTop(r => r.cm) },
    { name: "Service Partner", selector: r => r.sp, sortable: true, right: true,
      cell: r => <span>{money(r.sp)}</span>,
      sortFunction: sortWithInactiveTop(r => r.sp) },
    { name: "Service Manager", selector: r => r.sm, sortable: true, right: true,
      cell: r => <span>{money(r.sm)}</span>,
      sortFunction: sortWithInactiveTop(r => r.sm) },
    // { name: "Job Partner", selector: r => r.sp, sortable: true, right: true,
    //   cell: r => <span>{money(r.sp)}</span>,
    //   sortFunction: sortWithInactiveTop(r => r.sp) },
    // { name: "Job Manager", selector: r => r.sm, sortable: true, right: true,
    //   cell: r => <span>{money(r.sm)}</span>,
    //   sortFunction: sortWithInactiveTop(r => r.sm) },

    // Total (rightmost, not included in the header band)
    { name: "Total", selector: r => r.total, sortable: true, right: true,
      cell: r => <strong>{money(r.total)}</strong>,
      sortFunction: sortWithInactiveTop(r => r.total) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const conditionalRowStyles = useMemo(() => [
    { when: row => row.inactive === true, style: { backgroundColor: '#fef2f2', color: '#7f1d1d' } },
    // { when: row => row.isElective, style: { backgroundColor: '#fef9c3' } }// pale yellow 
    {
      // highlight staff row if they have ANY elective task
      when: row => staffWithElectiveTasks.has(row.empIdx),
      style: { backgroundColor: '#fef9c3' }, // pale yellow
    },
  ], [staffWithElectiveTasks]);

  // const keyToManualReassign = useMemo(() => {
  //   const keyToManualFlag = new Map();

  // }, []);

  /* expansion component for each employee row */
  // Ref holds latest closure deps so Expandable stays a stable reference (no DataTable remount)
  const _expandCtx = useRef({});
  _expandCtx.current = { usernameByIndex, electiveMap, buildClientRowsFor, downloadCsvFor, toggleElective, wipByClientCode };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const Expandable = useCallback(({ data }) => {
    const { usernameByIndex, electiveMap, buildClientRowsFor, downloadCsvFor, toggleElective, wipByClientCode } = _expandCtx.current;
    const staffIdx = data?.empIdx;
    const username = usernameByIndex(staffIdx);

    const isRowElective = (clientCode, roleChips) => {
      if (!username || !Array.isArray(roleChips)) return false;

      for (const chip of roleChips) {
        const role = chip.kind;
        const svc =
          role === "SP" || role === "SM"
            ? (chip.tag || "NA")
            : "NA";

        const key = keyify(clientCode, role, svc, username);
        if (electiveMap.has(key)) {
          return true;
        }
      }
      return false;
    };

    return (
      <EmployeeDetailPanel
        employeeName={data?.name}
        rows={buildClientRowsFor(data?.empIdx)}
        onDownloadCSV={() => downloadCsvFor(data?.empIdx)}
        iconURL={ICON_URL}
        onManualReassign={({ clientCode, assignments }) => {
        assignments.forEach(({ role, service }) => {
          toggleElective({
            clientCode,
            role,
            service: service || "NA",
            staffIdx: data.empIdx,
          });
        });
      }}
      isRowElective={isRowElective}
      wipByClientCode={wipByClientCode}
      />
    );
  }, []); // stable ref — reads fresh values from _expandCtx

  // close panel on Escape key
  useEffect(() => {
        if (!panelOpen) return;
        const onKey = (e) => e.key === "Escape" && closePanel();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
      }, [panelOpen]);

  const missingCount = useMemo(
    () => reassignmentTasks.reduce((n, t) => {
      if (!metadataByKey.has(t.key)) return n + 1;
      // Also count elective tasks whose metadata has a terminal status (needs re-seeding)
      if (t.isElective) {
        const wf = cn(metadataByKey.get(t.key), 'workflow', null);
        if (wf === WF.DRAFT_ACCEPTED || wf === WF.DRAFT_REJECTED) return n + 1;
      }
      return n;
    }, 0),
    [reassignmentTasks, metadataByKey]
  );

  //Debugging
  const missingKeys = useMemo(() => {
    const out = [];
    for (const t of reassignmentTasks) {
      if (!metadataByKey.has(t.key)) out.push(t.key);
    }
    return out;
  }, [reassignmentTasks, metadataByKey]);

  useEffect(() => {
    if (!panelOpen) return;
    console.log("[RPRS] missingCount:", missingKeys.length);
    console.log("[RPRS] missingKeys sample:", missingKeys.slice(0, 25));
    console.log("[RPRS] missingKeys:", missingKeys);
  }, [panelOpen, missingKeys]);


  //////////////////////////////

  useEffect(() => {
    if (panelOpen && missingCount > 0) {
      seedMissingWorkflowMetadata(reassignmentTasks);
    }
  }, [panelOpen, missingCount]); // <- not on raw metadataByKey reference

  // useEffect(() => {
  //   if (!panelOpen) return;
  //   // seed on open; ignore errors here or surface a toast as you like
  //   seedMissingWorkflowMetadata(reassignmentTasks).catch(console.error);
  // }, [panelOpen, reassignmentTasks, metadataByKey]);

  // Map: CLIENTCODE -> { clientCode, clientName, items: [{role, service, staffIdx, staffName}] }
  const clientRolesByCode = useMemo(() => {
    const m = new Map();
    const seen = new Set(); // dedupe CP/CM/SP/SM + service + staff

    for (const r of detail) {
      const clientCode = cn(r, "clientcode");
      if (!clientCode) continue;

      const clientName = cn(r, "clientname") || cn(r, "client") || "";
      const svcTag = getServiceTag(r) || "NA";

      const ensureBucket = () => {
        let bucket = m.get(clientCode);
        if (!bucket) {
          bucket = { clientCode, clientName, items: [] };
          m.set(clientCode, bucket);
        }
        return bucket;
      };

      // For each role, figure out who holds it on this row
      const roleSpecs = [
        { role: "CP", service: null },
        { role: "CM", service: null },
        { role: "SP", service: svcTag },
        { role: "SM", service: svcTag },
      ];

      for (const spec of roleSpecs) {
        const idx = resolveIndex(r, spec.role, nameToIndex);
        if (!Number.isFinite(idx)) continue;

        const key = `${clientCode}|${spec.role}|${spec.service || ""}|${idx}`;
        if (seen.has(key)) continue; // already captured this combo
        seen.add(key);

        const staffRow = empByIndex.get(idx) || {};
        const staffName = cn(staffRow, "staffname") || "";

        const bucket = ensureBucket();
        bucket.items.push({
          role: spec.role,
          service: spec.role === "SP" || spec.role === "SM" ? spec.service : null,
          staffIdx: idx,
          staffName,
        });
      }
    }

    // sort roles nicely inside each client
    for (const bucket of m.values()) {
      bucket.items.sort((a, b) =>
        a.role.localeCompare(b.role) ||
        String(a.service || "").localeCompare(String(b.service || "")) ||
        (a.staffName || "").localeCompare(b.staffName || "")
      );
    }

    return m;
  }, [detail, nameToIndex, empByIndex]);



  // ---------------- UI helpers for the wizard ----------------
  // Dashboard.js
  // const RolePill = ({ role, tags }) => {
  //   const label = {
  //     CP: "Client Partner",
  //     CM: "Client Manager",
  //     SP: "Service Partner",
  //     SM: "Service Manager",
  //   };
  //   const roleClass = {
  //     CP: styles.chip_cp,
  //     CM: styles.chip_cm,
  //     SP: styles.chip_sp,
  //     SM: styles.chip_sm,
  //   }[role] || "";

  //   // show “: ACCTG, INDTAX, …” for SP/SM (you already pass tags from the task)
  //   const suffix =
  //     (role === "SP" || role === "SM") && Array.isArray(tags) && tags.length
  //       ? `: ${tags.join(", ")}`
  //       : "";

  //   return (
  //     <span className={`${styles.chip} ${roleClass}`}>
  //       {label[role] || role}
  //       {suffix}
  //     </span>
  //   );
  // };
  

  // Format workflow timestamp for display (MM/DD/YYYY) and a hover tooltip in Central time
  const formatWfTs = (ts) => {
    if (!ts) return null;

    // Accepts: "YYYY-MM-DD HH:mm:ss.SSS -0700" or with/without millis
    // -> "YYYY-MM-DDTHH:mm:ss.SSS-07:00"
    const m = String(ts).match(
      /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?)\s+([+-]\d{2})(\d{2})$/
    );
    const iso = m
      ? `${m[1]}T${m[2]}${m[3]}:${m[4]}`
      : String(ts).replace(/^(\d{4}-\d{2}-\d{2})\s/, "$1T"); // last-resort

    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      // Still couldn’t parse — show as-is
      return { display: ts, title: ts };
    }

    // Cell: just the date (MM/DD/YYYY) in Central Time
    const display = d.toLocaleDateString("en-US", { timeZone: "America/Chicago" });

    // Tooltip: full timestamp in Central Time
    const title =
      d.toLocaleString("en-US", {
        timeZone: "America/Chicago",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }) + " CT";

    return { display, title };
  };



  // const RolePill = ({ role, tags }) => {
  //   const map = { CP: "Client Partner", CM: "Client Manager", SP: "Service Partner", SM: "Service Manager" };
  //   const hue = { CP: "#dbeafe", CM: "#e9d5ff", SP: "#fde68a", SM: "#bbf7d0" }[role] || "#e5e7eb";
  //   const suffix = (role === "SP" || role === "SM") && Array.isArray(tags) && tags.length ? `: ${tags.join(", ")}` : "";
  //   return (
  //     <span style={{
  //       background: hue,
  //       borderRadius: 9999, padding: "4px 10px", fontSize: 12, fontWeight: 700,
  //       border: "1px solid rgba(0,0,0,.05)"
  //     }}>
  //       {(map[role] || role) + suffix}
  //     </span>
  //   );
  // };

  // Given a staff idx and a roleKey ("total" | "cp" | "cm" | "sp" | "sm"),
  // return a Set of all staff indices directly involved in that change.
  const participantsForChange = (targetIdx, roleKey) => {
    const set = new Set([targetIdx]);
    const roleMap = { cp: "CP", cm: "CM", sp: "SP", sm: "SM" }; // our drafts store upper roles

    for (const { task, newStaffIdx } of drafts.values()) {
      if (!newStaffIdx || newStaffIdx === task.currentStaffIdx) continue;

      // If role-specific, skip non-matching drafts
      if (roleKey !== "total" && task.role !== roleMap[roleKey]) continue;

      const a = task.currentStaffIdx;
      const b = newStaffIdx;

      if (a === targetIdx || b === targetIdx) {
        set.add(a); set.add(b);
      }
    }
    return set;
  };

  //////////////////////////////////////////////////////

  // Build a Map of { taskId -> { task, newStaffIdx } } for the admin view
  const adminReviewDrafts = useMemo(() => {
    const m = new Map();

    // 1) existing “needs review” items (from anyone)
    for (const t of reassignmentTasks) {
      if (t.wfStatus === WF.DRAFT_NEEDS_REVIEW) {
        const idx =
          Number.isFinite(t.wfPendingIdx)
            ? t.wfPendingIdx
            : nameToIndex.get(normStrong(t.wfNewStaff ?? "")) ?? null;
        if (idx) m.set(t.id, { task: t, newStaffIdx: idx, source: "pending" });
      }
    }

    // 2) overlay the admin’s freshly picked changes (take precedence)
    for (const [taskId, d] of drafts) {
      if (d?.newStaffIdx /* && d.newStaffIdx !== d.task.currentStaffIdx */) {
        m.set(taskId, { task: d.task, newStaffIdx: d.newStaffIdx, source: "new" });
      }
    }

    return m;
  }, [reassignmentTasks, drafts, nameToIndex]);

  //////////////////////////////////////////////////////


  /* ---------------- UI ---------------- */
  return (
    <div className={`ab-rows ${panelOpen ? "tray-open" : ""}`}>
      {authError && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '40px 32px', maxWidth: 480, width: '90%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>&#128274;</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#003D4C', margin: '0 0 12px' }}>Access Restricted</h2>
            <p style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, margin: '0 0 16px' }}>
              {authError.includes('not authorized') || authError.includes('network')
                ? 'This application requires a connection through the BMSS network (Zscaler). Please ensure your VPN or Zscaler client is active.'
                : 'Your account is not authorized to access this application.'}
            </p>
            <p style={{ fontSize: 12, color: '#9CA3AF', margin: '0 0 20px' }}>
              If you believe this is an error, please contact:
            </p>
            <a href="mailto:dataanalyticsteam@bmss.com"
              style={{ display: 'inline-block', background: '#003D4C', color: '#fff', padding: '10px 24px', borderRadius: 8, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
              dataanalyticsteam@bmss.com
            </a>
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #E5E7EB' }}>
              <a href="/.auth/logout" style={{ fontSize: 12, color: '#6B7280', textDecoration: 'none' }}>
                Sign out and try a different account &rarr;
              </a>
            </div>
          </div>
        </div>
      )}
      <LoadingGame ready={!loading} />
      {/* TOP: main dashboard stays visible/usable */}
      <main className="ab-main">
        <div className="ab-dashboard">
        {/* Command bar */}
        <div className="ab-command-bar">
          <button
            type="button"
            className="ab-action-btn"
            aria-expanded={panelOpen}
            aria-controls="reassign-bottom-tray"
            onClick={togglePanel}
            title={panelOpen ? "Close Reassignment Wizard" : "Open Reassignment Wizard"}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 5h10"/><path d="M9 2l3 3-3 3"/><path d="M14 11H4"/><path d="M7 8l-3 3 3 3"/></svg>
            Reassignment Wizard
          </button>
          <button
            type="button"
            className="ab-action-btn"
            onClick={onDownloadImportTemplate}
            title="Download an Excel template for bulk elective reassignments"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 2v8"/><path d="M5 7l3 3 3-3"/><path d="M2 13h12"/></svg>
            Import Template
          </button>
          <button
            type="button"
            className="ab-action-btn"
            onClick={() => importInputRef.current?.click()}
            disabled={!!importBusy}
            title="Upload a completed Excel/CSV reassignment template to seed Draft tasks"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 10V2"/><path d="M5 5l3-3 3 3"/><path d="M2 13h12"/></svg>
            {importBusy ? "Importing…" : "Import Excel/CSV"}
          </button>
          <button
            type="button"
            className="ab-action-btn"
            onClick={() => setBulkElectiveOpen(true)}
            title="Seed multiple elective reassignment tasks at once"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="5" height="5" rx="1"/>
              <rect x="9" y="2" width="5" height="5" rx="1"/>
              <rect x="2" y="9" width="5" height="5" rx="1"/>
              <path d="M9 11.5h5M11.5 9v5"/>
            </svg>
            Bulk Elective
          </button>
          <button
            type="button"
            className="ab-kpi-toggle"
            onClick={() => setKpisCollapsed(v => !v)}
            title={kpisCollapsed ? "Show KPI summary cards" : "Hide KPI summary cards"}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d={kpisCollapsed ? "M2 4l4 4 4-4" : "M2 8l4-4 4 4"} />
            </svg>
            {kpisCollapsed ? "Show KPIs" : "Hide KPIs"}
          </button>
        </div>
        {/* KPIs */}
        <div className={`kpi-row${kpisCollapsed ? " kpi-collapsed" : ""}`}>
          <KpiShell title="Avg Billed / Employee" value={money(avgBilledPerEmployee)} />
          <KpiShell title="Avg WIP Hours / Employee" value={hoursFmt(avgWipHoursPerEmployee)} />
          <KpiShell title="Employee Count" value={filteredRows.length} />
          <KpiShell
            title="Requiring Reassignment"
            value={money(reassignmentTotal)}
            danger={reassignmentTotal > 0}
          />
        </div>

        {/* Filter Bar */}
        <section className="ab-filterbar" aria-label="Filters">
          <div className="fb-row">
            <div className="fb-field fb-search">
              <label>Search</label>
              <SearchInput
                className="ab-input"
                placeholder="Search name, dept, office, level…"
                value={filters.q}
                onChange={v => update("q", v)}
              />
            </div>

            {/*
            <div className="fb-field">
              <label>Department</label>
              <select
                className="ab-select"
                value={filters.dept}
                onChange={e => update("dept", e.target.value)}
              >
                {deptOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            */}
            <div className="fb-field">
              <label>Department</label>
              <MultiSelectDropdown
                options={deptOptions}
                selected={filters.dept}
                onChange={(vals) => update("dept", vals)}
                placeholder="All departments"
              />
            </div>


            {/*
            <div className="fb-field">
              <label>Office</label>
              <select
                className="ab-select"
                value={filters.office}
                onChange={e => update("office", e.target.value)}
              >
                {officeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            */}
            <div className="fb-field">
              <label>Office</label>
              <MultiSelectDropdown
                options={officeOptions}
                selected={filters.office}
                onChange={(vals) => update("office", vals)}
                placeholder="All offices"
              />
            </div>


            {/*
            <div className="fb-field">
              <label>Level</label>
              <select
                className="ab-select"
                value={filters.level}
                onChange={e => update("level", e.target.value)}
              >
                {levelOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            */}
            <div className="fb-field">
              <label>Level</label>
              <MultiSelectDropdown
                options={levelOptions}
                selected={filters.level}
                onChange={(vals) => update("level", vals)}
                placeholder="All levels"
              />
            </div>


            <div className="fb-field fb-number">
              <label>Min Total</label>
              <input
                className="ab-input"
                type="number"
                inputMode="numeric"
                min="0"
                placeholder="0"
                value={filters.minTotal}
                onChange={e => update("minTotal", e.target.value)}
              />
            </div>

            <div className="fb-field fb-toggle">
              <label>&nbsp;</label>
              <label className="ab-toggle">
                <input
                  type="checkbox"
                  checked={filters.inactiveOnly}
                  onChange={e => update("inactiveOnly", e.target.checked)}
                />
                Inactive only
              </label>
            </div>

            <div className="fb-field fb-toggle">
              <label>&nbsp;</label>
              <label className="ab-toggle">
                <input
                  type="checkbox"
                  checked={filters.mgmtOnly}
                  onChange={e => update("mgmtOnly", e.target.checked)}
                />
                Management group only
              </label>
            </div>
          </div>
        </section>

        {focusStaff.size > 0 && (
          <div className="ab-chiprow" style={{margin: "6px 0 10px"}}>
            <span className="ab-chip ghost">Focused view</span>
            {[...focusStaff].map(ix => (
              <span key={ix} className="ab-chip">
                {/* {empByIndex.get(ix)?.STAFFNAME || ix} */}
                {cn(empByIndex.get(ix), "staffname") || ix}
              </span>
            ))}
            <button className="ab-chip" onClick={clearFocus}>Clear</button>
          </div>
        )}
      
        {/* Grouped header bar aligned to the 4 money columns */}
        <div className="money-group-wrapper">
          {/* <MoneyGroupHeader /> */}
          {/* <MoneyGroupHeader onToggle={togglePanel} panelOpen={panelOpen} iconUrl={ICON_URL} /> */}
          <MoneyGroupHeader />
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              // allow selecting the same file twice
              e.target.value = "";
              if (!f) return;
              await importRowsToTasks(f);
            }}
          />

          {importSummary && (
            <div className="ab-muted" style={{ marginTop: 6 }}>
              Imported {importSummary.written} tasks (planned {importSummary.plannedTasks}). Row issues: {importSummary.rowProblems}. Write failures: {importSummary.writeFailures}.
            </div>
          )}
        </div>

        {/* Table */}
        <DataTable
          title={null}
          columns={columns}
          data={filteredRows}
          progressPending={loading}
          pagination
          highlightOnHover
          striped
          conditionalRowStyles={conditionalRowStyles}
          /* NEW: expansion wired in (react-data-table-component style API) */
          expandableRows
          expandableRowsComponent={Expandable}
        />
      </div>
      </main>

      {/* BOTTOM: resizable Reassignment Wizard (no backdrop, no outside-click close) */}
      <BulkElectiveModal
        open={bulkElectiveOpen}
        onClose={() => setBulkElectiveOpen(false)}
        onConfirm={handleBulkElectiveConfirm}
        empByIndex={empByIndex}
        userToIndex={userToIndex}
        detail={detail}
        clientGroups={clientGroups}
        isAdmin={isAdmin}
        currentUser={CURRENT_USER}
      />
      <WizardShell
        isOpen={panelOpen}
        trayVh={trayVh}
        onClose={togglePanel}
        onStartDrag={onDragStart}
        onDragMove={onDragMove}
        onEndDrag={onDragEnd}
        tab={wizardTab}
        onTabChange={setWizardTab}
        // tabs={isAdmin ? ["drafts","summary","pending"] : ["drafts","summary"]}
        // admins still see just Drafts + Summary, but Summary is a different component
        tabs={["drafts","summary"]}
      >
        {wizardTab === "drafts" && (
          <>
            {/* <DraftTable /> */}
            <DraftsStep
              tasks={wizardTasks}
              wizardLoading={wizardLoading}
              filters={rwzFilters}
              onChangeFilters={setRwzFilters}
              drafts={drafts}
              onPick={(task, newStaffIdx) =>
                setDrafts((prev) => {
                  const next = new Map(prev);
                  next.set(task.id, { task, newStaffIdx });
                  return next;
                })
              }
              empByIndex={empByIndex}
              nameToIndex={nameToIndex}
              isAdmin={isAdmin}
              userToName={userToName}
              clientRolesByCode={clientRolesByCode}
              onDelete={handleDeleteTask}
              // formatWfTs={formatWfTs}
            />
            {/* <div className="wiz-footer">
              <button
                className="ab-btn"
                disabled={reassignmentTasks.length === 0}
                onClick={() => setWizardTab("summary")}
              >
                Next
              </button>
            </div> */}
            <div className="wiz-footer">
              <div className="wiz-footer-left">
                <div className="bulk-reassign">
                  <span className="bulk-reassign-label">Bulk Reassign</span>
                  <div className="bulk-reassign-select">
                    <StaffSelect
                      value={bulkIdx ?? ""}
                      onChange={applyBulk}
                      empByIndex={empByIndex}
                      disabled={filteredTasksForBulk.length === 0}
                      title={"Select staff reassignment for currently filtered tasks"}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="ab-btn ghost"
                  onClick={clearAllSelections}
                  disabled={drafts.size === 0}
                  title="Clear all selected reassignment staff (ignores filters)"
                >
                  Clear All Selections
                </button>
              </div>

              <button
                className="ab-btn"
                disabled={reassignmentTasks.length === 0}
                onClick={() => setWizardTab("summary")}
              >
                Next
              </button>
            </div>
          </>
        )} 
        {wizardTab === "summary" && (
          <>
            {/* <SummaryStep
              rows={rows}
              drafts={drafts}
              empByIndex={empByIndex}
              //money2={money2}
              openStaff={openStaff}
              toggleStaff={toggleStaff}
              participantsForChange={participantsForChange}
              onFocusParticipants={setFocusStaff}  // so the funnel filters the main table
            /> */}
            {isAdmin ? (
              <AdminSummaryStep
                rows={rows}
                // drafts={drafts}
                drafts={adminReviewDrafts} // Include needs review tasks and changes just made
                empByIndex={empByIndex}
                openStaff={openStaff}
                toggleStaff={toggleStaff}
                participantsForChange={participantsForChange}
                onFocusParticipants={setFocusStaff}
                onDecisionsChange={setAdminDecisions}
              />
            ) : (
              <SummaryStep
                rows={rows}
                drafts={drafts}
                empByIndex={empByIndex}
                openStaff={openStaff}
                toggleStaff={toggleStaff}
                participantsForChange={participantsForChange}
                onFocusParticipants={setFocusStaff}
              />
            )}

            <div className="wiz-footer">
              <button className="ab-btn ghost" onClick={() => setWizardTab("drafts")}>Back</button>

              {isAdmin ? (
                <button
                  className="ab-btn"
                  disabled={
                    missingCount > 0 //||
                    //!reassignmentTasks.some(t => t.wfStatus === WF.DRAFT_NEEDS_REVIEW)
                  }
                  onClick={async () => {
                    const toApply = [...adminDecisions.entries()]; // [ [taskId, 'accept'|'reject'], ... ]
                    if (!toApply.length) return;
                    setSubmitting(true);

                    // Record admin decisions and update workflow metadata
                    const entries = await Promise.all(
                      toApply.map(async ([taskId, choice]) => {
                        const d = adminReviewDrafts.get(taskId);
                        if (!d) return null;
                        const intent = choice === "accept" ? "admin_accept" : "admin_reject";
                        return updateWorkflow({
                          intent,
                          task: d.task,
                          newStaffIdx: d.newStaffIdx,       // accept path needs the selected person
                          actor: CURRENT_USER,              // who is making the decision
                          routeTo: choice === "reject" ? routeToFor(d.task) : CURRENT_USER, // Give back to original assigned person
                          reviewer: CURRENT_USER,           // same for now
                          empByIndex,
                          isAdmin: true
                        });
                      })
                    );

                    // Build list of accepted changes to either CP or CM and write to PE
                    const acceptedClientChanges = [];
                    const acceptedServiceChanges = [];
                    for (const [taskId, choice] of toApply) {
                      if (choice !== "accept") continue;
                      const d = adminReviewDrafts.get(taskId);
                      // console.log("d:", d);
                      if (!d) continue;
                      const {task, newStaffIdx} = d;
                      // if (task.role !== "CP" && task.role !== "CM") continue;
                      // console.log("task:", task);
                      // console.log("task.role:", task.role);
                      if (task.role === "CP" || task.role === "CM") {
                        const baseJson = buildEntry({
                          task,
                          newStaffIdx,
                          empByIndex,
                          fromUser: CURRENT_USER,
                          toUser: CURRENT_USER,
                          status: WF.DRAFT_ACCEPTED
                        });
                        acceptedClientChanges.push(baseJson)
                      } else if (task.role === "SP" || task.role === "SM"){
                          const baseJson = buildEntry({
                          task,
                          newStaffIdx,
                          empByIndex,
                          fromUser: CURRENT_USER,
                          toUser: CURRENT_USER,
                          status: WF.DRAFT_ACCEPTED
                        });
                        acceptedServiceChanges.push(baseJson)
                      } else continue
                    }
                    if (acceptedClientChanges.length) {
                      await writeToPE("client", {
                        submittedBy: CURRENT_USER,
                        items: acceptedClientChanges
                      });
                    }
                    if (acceptedServiceChanges.length) {
                      await writeToPE("service", {
                        submittedBy: CURRENT_USER,
                        items: acceptedServiceChanges
                      });
                    }

                    setMetadata(prev => prev.concat(entries.filter(Boolean)));
                    setAdminDecisions(new Map()); // clear selections
                    // Drop the handled drafts so the rows disappear
                    setDrafts(prev => {
                      const next = new Map(prev);
                      for (const [taskId] of toApply) next.delete(taskId);
                      return next;
                    });
                    // Clear submitted elective keys so next bulk confirm starts fresh
                    setElectiveMap(prev => {
                      const next = new Map(prev);
                      for (const [taskId] of toApply) {
                        const d = adminReviewDrafts.get(taskId);
                        if (d?.task?.key) next.delete(d.task.key);
                      }
                      return next;
                    });
                    setSubmitting(false);
                    setSubmitting(false);
                    setSubmitToast(true);
                    setWizardTab("drafts");
                  }}
                >
                  Confirm All Choices
                </button>
              ) : (
                <button
                  className="ab-btn"
                  disabled={(missingCount > 0) || !([...drafts.values()].some(v => v.newStaffIdx))}
                  onClick={async () => {
                    setSubmitting(true);
                    // gather only the drafts where a NEW staff has been chosen
                    const chosen = [...drafts.values()]
                      .filter(d => d.newStaffIdx /* && d.newStaffIdx !== d.task.currentStaffIdx */);

                    const entries = await Promise.all(
                      chosen.map(({ task, newStaffIdx }) =>
                        updateWorkflow({
                          intent: "owner_submit",
                          task,
                          newStaffIdx,           // lets updateWorkflow resolve NEW_STAFF_SELECTION
                          actor: CURRENT_USER,   // the submitting non-admin
                          routeTo: "sysadmin",   // or your committee username
                          empByIndex,            // so names are resolved from index
                        })
                      )
                    );
                    // merge locally so Admin columns refresh immediately
                    setMetadata(prev => prev.concat(entries));
                    // optional UX
                    setDrafts(new Map());
                    setElectiveMap(new Map()); // clear submitted elective keys
                    setSubmitToast(true);
                    setWizardTab("drafts");
                  }}
                >
                  Confirm
                </button>
              )}


              {/* <button
                className="ab-btn"
                disabled={
                  (missingCount > 0) ||
                  !([...drafts.values()].some(v => v.newStaffIdx))
                }
                onClick={async () => {
                  // gather only the drafts where a NEW staff has been chosen
                  const chosen = [...drafts.values()]
                    .filter(d => d.newStaffIdx && d.newStaffIdx !== d.task.currentStaffIdx);

                  const entries = await Promise.all(
                    chosen.map(({ task, newStaffIdx }) =>
                      updateWorkflow({
                        intent: "owner_submit",
                        task,
                        newStaffIdx,           // lets updateWorkflow resolve NEW_STAFF_SELECTION
                        actor: CURRENT_USER,   // the submitting non-admin
                        routeTo: "sysadmin",   // or your committee username
                        empByIndex,            // so names are resolved from index
                      })
                    )
                  );
                  // merge locally so Admin columns refresh immediately
                  setMetadata(prev => prev.concat(entries));
                  // optional UX
                  setDrafts(new Map());
                  setWizardTab("drafts");
                }}
              >
                Confirm
              </button> */}
              {missingCount > 0 && (
                <span className="ab-muted" style={{ marginLeft: 8 }}>
                  Seeding {missingCount} metadata…
                </span>
              )}
            </div>
          </>
        )}
      {submitting && (
        <div className="submit-toast">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="drfts-spinner" />
            <span>Submitting reassignments…</span>
          </div>
        </div>
      )}
      {submitToast && (
        <div className="submit-toast">
          <button className="submit-toast-close" onClick={() => setSubmitToast(false)}>×</button>
          <strong>Submitted!</strong> Depending on batch size, it may take a few minutes for all changes to be reflected in Practice Engine.
        </div>
      )}
      </WizardShell>
    </div>
  );
}